import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  LayoutDashboard, Image, Package, MessageSquare, Languages, 
  FileText, LogOut, Menu, X, Save, Plus, Trash2, Edit2, Edit,
  Eye, Check, AlertCircle, Upload, Link, Bell, Mail, Phone, TestTube, Calendar,
  DollarSign, Building, User, MapPin, Copy, Printer, Send, RefreshCw, TrendingUp, Calculator, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import CommercialDisclosureTab from './admin/CommercialDisclosureTab';
import PrivacyPolicyTab from './admin/PrivacyPolicyTab';
import FlyerGenerator from './admin/FlyerGenerator';
import SignQuotesTab from './admin/SignQuotesTab';
import BusinessInquiriesTab from './admin/BusinessInquiriesTab';
import BrandBoostInquiriesTab from './admin/BrandBoostInquiriesTab';
import SalesCalculator from './admin/SalesCalculator';
import NeonLogoGenerator from './admin/NeonLogoGenerator';
import SavedQuotesTab from './admin/SavedQuotesTab';
import QuoteGeneratorTab from './admin/QuoteGeneratorTab';
import QuoteRebranderTab from './admin/QuoteRebranderTab';
import { generateQuotePDF } from './admin/quotePdfGenerator';
import AnalyticsTrackingTab from './admin/AnalyticsTrackingTab';

// Import static translation files as defaults
import enTranslations from '../locales/en.json';
import jaTranslations from '../locales/ja.json';

// Use window.location.origin to always call the same domain
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Edit Modal Component
const EditModal = ({ isOpen, onClose, title, children, onSave, saving, isDark }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
        style={{
          backgroundColor: isDark ? '#111111' : '#ffffff',
          border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
          boxShadow: isDark 
            ? '0 20px 60px rgba(255, 0, 255, 0.3)' 
            : '0 20px 60px rgba(255, 20, 147, 0.2)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            className="text-xl font-bold"
            style={{ color: isDark ? '#ffffff' : '#0f172a' }}
          >
            {title}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
            style={{ color: isDark ? '#666' : '#94a3b8' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {children}
        </div>
        
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
          <Button
            onClick={onClose}
            variant="outline"
            style={{
              borderColor: isDark ? '#666' : '#e2e8f0',
              color: isDark ? '#a0a0a0' : '#64748b'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-2"
            style={{
              background: isDark 
                ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)'
            }}
          >
            {saving ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Input Field Component
const InputField = ({ label, value, onChange, type = 'text', placeholder, isDark, multiline = false, rows = 3 }) => (
  <div>
    <label 
      className="block text-sm font-medium mb-2"
      style={{ color: isDark ? '#a0a0a0' : '#374151' }}
    >
      {label}
    </label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-4 py-3 rounded-lg resize-none"
        style={{
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
          border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
          color: isDark ? '#ffffff' : '#0f172a'
        }}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
          border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
          color: isDark ? '#ffffff' : '#0f172a'
        }}
      />
    )}
  </div>
);

// Image Uploader Component with Drag & Drop
const ImageUploader = ({ onImageSelected, isDark, uploading }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadMode, setUploadMode] = useState('drag'); // 'drag' or 'url'
  const [urlInput, setUrlInput] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
    
    // Pass file to parent
    onImageSelected({ type: 'file', file });
  };

  const handleUrlSubmit = () => {
    if (urlInput) {
      setPreviewUrl(urlInput);
      onImageSelected({ type: 'url', url: urlInput });
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setUploadMode('drag')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-all ${
            uploadMode === 'drag' ? 'scale-105' : ''
          }`}
          style={{
            backgroundColor: uploadMode === 'drag' 
              ? (isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.1)')
              : (isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'),
            border: `2px solid ${uploadMode === 'drag' ? (isDark ? '#ff00ff' : '#ff1493') : (isDark ? '#333' : '#e2e8f0')}`,
            color: uploadMode === 'drag' ? (isDark ? '#ff00ff' : '#ff1493') : (isDark ? '#a0a0a0' : '#64748b')
          }}
        >
          <Upload className="w-4 h-4" />
          Upload File
        </button>
        <button
          onClick={() => setUploadMode('url')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-all ${
            uploadMode === 'url' ? 'scale-105' : ''
          }`}
          style={{
            backgroundColor: uploadMode === 'url' 
              ? (isDark ? 'rgba(0, 255, 255, 0.2)' : 'rgba(0, 191, 255, 0.1)')
              : (isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'),
            border: `2px solid ${uploadMode === 'url' ? (isDark ? '#00ffff' : '#00bfff') : (isDark ? '#333' : '#e2e8f0')}`,
            color: uploadMode === 'url' ? (isDark ? '#00ffff' : '#00bfff') : (isDark ? '#a0a0a0' : '#64748b')
          }}
        >
          <Link className="w-4 h-4" />
          Image URL
        </button>
      </div>

      {/* Drag & Drop Zone */}
      {uploadMode === 'drag' && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
            dragActive ? 'scale-105' : 'hover:scale-102'
          }`}
          style={{
            borderColor: dragActive 
              ? (isDark ? '#ff00ff' : '#ff1493')
              : (isDark ? '#444' : '#cbd5e1'),
            backgroundColor: dragActive 
              ? (isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(255, 20, 147, 0.05)')
              : (isDark ? 'rgba(0, 0, 0, 0.3)' : '#f8fafc')
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden"
          />
          
          <div className="flex flex-col items-center gap-3">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.1)',
              }}
            >
              <Upload 
                className="w-8 h-8" 
                style={{ color: isDark ? '#ff00ff' : '#ff1493' }} 
              />
            </div>
            <div>
              <p 
                className="font-semibold"
                style={{ color: isDark ? '#ffffff' : '#0f172a' }}
              >
                {dragActive ? 'Drop image here' : 'Drag & drop image here'}
              </p>
              <p 
                className="text-sm mt-1"
                style={{ color: isDark ? '#666' : '#94a3b8' }}
              >
                or click to browse
              </p>
            </div>
            <p 
              className="text-xs"
              style={{ color: isDark ? '#444' : '#cbd5e1' }}
            >
              Supports: JPG, PNG, GIF, WEBP
            </p>
          </div>

          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
              <div className="flex items-center gap-3 text-white">
                <div className="animate-spin w-6 h-6 border-3 border-white border-t-transparent rounded-full" />
                Uploading...
              </div>
            </div>
          )}
        </div>
      )}

      {/* URL Input */}
      {uploadMode === 'url' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-4 py-3 rounded-lg"
              style={{
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
                color: isDark ? '#ffffff' : '#0f172a'
              }}
            />
            <Button
              onClick={handleUrlSubmit}
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #00ffff 0%, #00bfff 100%)'
                  : 'linear-gradient(135deg, #00bfff 0%, #0099ff 100%)'
              }}
            >
              Use URL
            </Button>
          </div>
        </div>
      )}

      {/* Preview */}
      {previewUrl && (
        <div className="mt-4">
          <p 
            className="text-sm mb-2 font-medium"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            Preview:
          </p>
          <div className="relative rounded-lg overflow-hidden">
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="w-full h-48 object-cover rounded-lg"
              onError={(e) => { 
                e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image'; 
              }}
            />
            <button
              onClick={() => { setPreviewUrl(''); setUrlInput(''); }}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Toast Notification Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div 
      className={`fixed bottom-6 right-6 px-6 py-4 rounded-xl shadow-2xl z-[100] flex items-center gap-3 animate-in slide-in-from-right ${
        type === 'success' ? 'bg-green-500' : 'bg-red-500'
      }`}
    >
      {type === 'success' ? <Check className="w-5 h-5 text-white" /> : <AlertCircle className="w-5 h-5 text-white" />}
      <span className="text-white font-medium">{message}</span>
    </div>
  );
};

// Inline Image Uploader for Edit Modals
const InlineImageUploader = ({ value, onChange, label, isDark, apiUrl, authFetch: authFetchProp }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreviewUrl(value || '');
  }, [value]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    setUploading(true);
    
    try {
      // Create FormData and upload
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await authFetchProp(`${apiUrl}/api/admin/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url.startsWith('http') ? data.url : `${apiUrl}${data.url}`;
        setPreviewUrl(imageUrl);
        onChange(imageUrl);
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    }
    
    setUploading(false);
  };

  const handleClear = () => {
    setPreviewUrl('');
    onChange('');
  };

  return (
    <div className="mb-4">
      <label 
        className="block text-sm font-medium mb-2"
        style={{ color: isDark ? '#e0e0e0' : '#334155' }}
      >
        {label}
      </label>
      
      {previewUrl ? (
        <div className="relative">
          <img 
            src={previewUrl} 
            alt="Preview" 
            className="w-full h-40 object-cover rounded-lg"
            style={{ border: `2px solid ${isDark ? '#444' : '#e2e8f0'}` }}
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80"
              title="Change image"
            >
              <Upload className="w-4 h-4" />
            </button>
            <button
              onClick={handleClear}
              className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-600"
              title="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden"
          />
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200 ${
            dragActive ? 'scale-[1.02]' : 'hover:scale-[1.01]'
          }`}
          style={{
            borderColor: dragActive 
              ? (isDark ? '#ff00ff' : '#ff1493')
              : (isDark ? '#444' : '#cbd5e1'),
            backgroundColor: dragActive 
              ? (isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(255, 20, 147, 0.05)')
              : (isDark ? 'rgba(0, 0, 0, 0.3)' : '#f8fafc')
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden"
          />
          
          <div className="flex flex-col items-center gap-2">
            <Upload 
              className="w-8 h-8" 
              style={{ color: isDark ? '#ff00ff' : '#ff1493' }} 
            />
            <p 
              className="font-medium text-sm"
              style={{ color: isDark ? '#ffffff' : '#0f172a' }}
            >
              {dragActive ? 'Drop image here' : 'Drag & drop or click to upload'}
            </p>
            <p 
              className="text-xs"
              style={{ color: isDark ? '#666' : '#94a3b8' }}
            >
              JPG, PNG, GIF, WEBP
            </p>
          </div>

          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <div className="flex items-center gap-2 text-white text-sm">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                Uploading...
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { isDark } = useTheme();
  
  // Initialize user from localStorage only (mobile Safari compatible)
  const getInitialUser = () => {
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  
  const [user, setUser] = useState(getInitialUser);
  const [isAuthenticated, setIsAuthenticated] = useState(getInitialUser() ? true : null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Helper for authenticated API calls (supports both cookie and token)
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('adminToken');
    const headers = {
      ...options.headers,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
    return fetch(url, { ...options, credentials: 'include', headers });
  }, []);
  
  // Data states
  const [quotes, setQuotes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState({});
  const [translations, setTranslations] = useState({ en: enTranslations, ja: jaTranslations });
  
  // Business Studio state
  const [businessStudioSettings, setBusinessStudioSettings] = useState({
    enabled: true,
    sectionTitle: 'Business Studio',
    sectionSubtitle: 'Comprehensive business support services'
  });
  
  // Rentals state
  const [rentalSettings, setRentalSettings] = useState({
    enabled: false,
    sectionTitle: 'Sign Rentals',
    sectionSubtitle: 'Rent our beautiful signs for your special events',
    contactEmail: '',
    contactPhone: ''
  });
  const [rentalItems, setRentalItems] = useState([]);
  const [editingRentalItem, setEditingRentalItem] = useState(null);
  const [newRentalItem, setNewRentalItem] = useState({
    name: '',
    description: '',
    image: '',
    pricePerDay: '',
    pricePerWeekend: '',
    category: 'wedding',
    available: true
  });
  const [uploadingRentalImage, setUploadingRentalImage] = useState(false);
  const [editingRentalImage, setEditingRentalImage] = useState(false);
  
  // Modal states
  const [editModal, setEditModal] = useState({ open: false, type: '', data: null });
  const [addImageModal, setAddImageModal] = useState(false);
  const [newImageTitle, setNewImageTitle] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: false,
    email_address: '',
    gmail_address: '',
    gmail_app_password: '',
    sms_enabled: false,
    phone_number: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: ''
  });
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  
  // Quote Generator state
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Okinawa Sign Studio',
    company_name_local: '沖縄サインスタジオ',
    contact_person: '',
    address: '',
    phone: '',
    email: '',
    whatsapp: '',
    logo_url: '',
    paypal_email: '',
    bank_details: '',
    default_currency: 'USD'
  });
  const [generatedQuotes, setGeneratedQuotes] = useState([]);
  const [editingGeneratedQuote, setEditingGeneratedQuote] = useState(null);
  const [showQuotePreview, setShowQuotePreview] = useState(false);
  const [pendingEditQuote, setPendingEditQuote] = useState(null);
  const [savingCompanySettings, setSavingCompanySettings] = useState(false);
  const [savingGeneratedQuote, setSavingGeneratedQuote] = useState(false);
  const [newGeneratedQuote, setNewGeneratedQuote] = useState({
    quote_request_id: null,
    customer_name: '',
    customer_company: '',
    customer_contact: '',
    customer_address: '',
    customer_phone: '',
    customer_email: '',
    customer_whatsapp: '',
    line_items: [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
    images: [],
    shipping_cost: 0,
    tax_rate: 0,
    processing_time: '10-15 Working days',
    delivery_time: '5-7 working days',
    delivery_method: 'Express',
    payment_terms: 'DAP - Delivered At Place',
    notes: '',
    currency: 'JPY',
    installation_location: 'inside',
    mounting_style: '',
    include_warranty: false,
    include_shipping: true
  });
  const [uploadingQuoteImage, setUploadingQuoteImage] = useState(false);
  const [isDraggingQuoteImage, setIsDraggingQuoteImage] = useState(false);
  const quoteImageInputRef = useRef(null);

  // Quote Rebrander state
  const [rebranderFile, setRebranderFile] = useState(null);
  const [rebranderLoading, setRebranderLoading] = useState(false);
  const [rebranderExtractedData, setRebranderExtractedData] = useState(null);
  const [rebranderMarkup, setRebranderMarkup] = useState(30); // Default 30% markup
  const [rebranderCurrency, setRebranderCurrency] = useState('USD');
  const [isDraggingRebrander, setIsDraggingRebrander] = useState(false);
  const rebranderFileInputRef = useRef(null);

  // Link to Quote Request state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');

  // Default content structure
  const defaultContent = {
    hero: {
      badge: 'Premium Custom Signs',
      title1: 'Okinawa Sign',
      title2: 'Studio',
      subtitle: 'Transform your space with stunning custom signs. From LED neon to channel letters, we craft the perfect sign for your vision.',
      ctaNeon: 'Try Neon Customizer',
      ctaQuote: 'Get a Free Quote',
      backgroundImage: ''
    },
    features: {
      title: 'Why Choose',
      titleHighlight: 'Okinawa Sign Studio?',
      subtitle: 'We combine premium quality, innovative design, and exceptional customer service',
      feature1_title: 'Premium Quality',
      feature1_description: 'Highest quality materials and craftsmanship for signs that last',
      feature1_icon: 'Award',
      feature2_title: 'Custom Design',
      feature2_description: 'Every sign uniquely designed to match your vision perfectly',
      feature2_icon: 'Palette',
      feature3_title: 'Fast Turnaround',
      feature3_description: 'Quick production without compromising on quality',
      feature3_icon: 'Clock',
      feature4_title: 'Expert Support',
      feature4_description: 'Dedicated team to guide you from concept to installation',
      feature4_icon: 'Users'
    },
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Join hundreds of satisfied customers who transformed their spaces with our custom signs',
      testimonial1_name: 'Sarah Johnson',
      testimonial1_role: 'Restaurant Owner',
      testimonial1_text: 'The neon sign transformed our restaurant atmosphere completely. Customers love taking photos with it!',
      testimonial1_rating: '5',
      testimonial1_image: '',
      testimonial2_name: 'Mike Chen',
      testimonial2_role: 'Retail Store Manager',
      testimonial2_text: 'Professional service from start to finish. Our storefront sign increased foot traffic by 40%.',
      testimonial2_rating: '5',
      testimonial2_image: '',
      testimonial3_name: 'Emily Davis',
      testimonial3_role: 'Event Planner',
      testimonial3_text: 'Perfect for our wedding business. The custom signs add that special touch our clients love.',
      testimonial3_rating: '5',
      testimonial3_image: ''
    },
    footer: {
      description: 'Premium custom signs crafted with passion. From LED neon to channel letters, we bring your vision to life.',
      address: 'We Come 2 You, Okinawa, Japan',
      phone: '+81 XXX-XXXX-XXXX',
      email: 'okisignstudio@gmail.com',
      instagramUrl: 'https://www.instagram.com/okinawasignstudio/',
      facebookUrl: 'https://facebook.com'
    },
    logoDesign: {
      heroTitle: 'Logo Design & Enhancement',
      heroSubtitle: 'Your logo is the cornerstone of your brand identity—the first impression that can make or break customer trust.',
      heroImage: ''
    },
    brandBoost: {
      heroTitle: 'Business Enhancement',
      heroSubtitle: 'Elevate your business image, amplify your advertising impact, and create a brand presence that drives real growth.',
      heroImage: ''
    }
  };

  // Default products
  const defaultProducts = [
    { id: 'led-neon-signs', slug: 'led-neon-signs', name: 'LED Neon Signs', description: 'Modern LED neon signs that capture the classic neon aesthetic with modern technology. Safe, cool to touch, and incredibly vibrant.', image: 'https://images.unsplash.com/photo-1562226581-8afad1af1f3a?w=800', features: ['Classic neon look with LED technology', 'Safe and cool to touch', '30+ vibrant color options', 'Dimmable brightness', 'Custom text and designs'] },
    { id: 'led-signs', slug: 'led-signs', name: 'LED Signs', description: 'Bright, energy-efficient LED signs perfect for businesses, events, and home decor.', image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800', features: ['Energy efficient LED technology', 'Bright and vibrant illumination', 'Long lifespan (50,000+ hours)', 'Indoor and outdoor options'] },
    { id: 'channel-letter-signs', slug: 'channel-letter-signs', name: 'Channel Letter Signs', description: 'Professional 3D channel letter signs for storefronts and businesses.', image: 'https://images.unsplash.com/photo-1584444279613-a2c76483b6cc?w=800', features: ['3D dimensional letters', 'Front-lit, back-lit, or halo-lit options', 'Durable aluminum construction', 'Weather-resistant'] },
    { id: 'acrylic-signs', slug: 'acrylic-signs', name: 'Acrylic Signs', description: 'Sleek and modern acrylic signs perfect for offices, lobbies, and professional spaces.', image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800', features: ['Premium acrylic material', 'Clear, frosted, or colored options', 'Modern and professional look', 'Laser-cut precision'] },
    { id: 'metal-signs', slug: 'metal-signs', name: 'Metal Signs', description: 'Durable and timeless metal signs crafted from premium materials.', image: 'https://images.unsplash.com/photo-1630644964646-6cc8ac5b47ea?w=800', features: ['Premium aluminum or steel', 'Rust-resistant coating', 'Brushed, polished, or painted finishes', 'Indoor and outdoor durability'] },
    { id: 'lightbox-signs', slug: 'lightbox-signs', name: 'Lightbox Signs', description: 'Eye-catching illuminated lightbox signs that make your brand stand out day and night.', image: 'https://images.unsplash.com/photo-1582018960590-f3bc3ea25c04?w=800', features: ['Even LED illumination', 'Single or double-sided options', 'Easy graphic changes', 'Slim profile design'] },
    { id: 'custom-signs', slug: 'custom-signs', name: 'Any Custom Sign', description: 'Have a unique vision? We create fully custom signs tailored to your exact specifications.', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800', features: ['Fully customizable design', 'Any material combination', 'Unique shapes and sizes', 'Professional design consultation'] }
  ];

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Get token from localStorage (critical for mobile Safari where cookies don't work)
      const token = localStorage.getItem('adminToken');
      const storedUser = localStorage.getItem('adminUser');
      
      // No token at all = definitely not authenticated
      if (!token) {
        setIsAuthenticated(false);
        window.location.href = '/admin/login';
        return;
      }
      
      // We have a token - try to validate it with the server
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          // Token is valid - use server data
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
          // Store clean user data
          const cleanUser = {
            user_id: String(userData.user_id || ''),
            username: String(userData.username || ''),
            email: String(userData.email || ''),
            name: String(userData.name || userData.username || ''),
            picture: userData.picture ? String(userData.picture) : '',
            role: String(userData.role || 'admin')
          };
          localStorage.setItem('adminUser', JSON.stringify(cleanUser));
        } else if (response.status === 401) {
          // Token is invalid/expired - clear and redirect
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          setIsAuthenticated(false);
          window.location.href = '/admin/login';
        } else {
          // Server error but we have a token - trust local data for mobile resilience
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
              setIsAuthenticated(true);
              console.log('Using cached user data due to server error');
            } catch (e) {
              setIsAuthenticated(false);
              window.location.href = '/admin/login';
            }
          } else {
            setIsAuthenticated(false);
            window.location.href = '/admin/login';
          }
        }
      } catch (networkError) {
        // Network error (offline, etc.) - trust local data if we have it
        console.log('Network error during auth check:', networkError);
        if (storedUser && token) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
            console.log('Using cached user data due to network error');
          } catch (e) {
            setIsAuthenticated(false);
            window.location.href = '/admin/login';
          }
        } else {
          setIsAuthenticated(false);
          window.location.href = '/admin/login';
        }
      }
    };

    checkAuth();
  }, []);

  // Fetch quotes function (reusable)
  const fetchQuotes = async () => {
    try {
      const quotesRes = await authFetch(`${API_URL}/api/admin/quotes`);
      if (quotesRes.ok) {
        setQuotes(await quotesRes.json());
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  // Fetch overview data on initial load
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchOverviewData = async () => {
      try {
        const [quotesRes, galleryRes, productsRes, genQuotesRes] = await Promise.all([
          authFetch(`${API_URL}/api/admin/quotes`),
          authFetch(`${API_URL}/api/admin/gallery`),
          authFetch(`${API_URL}/api/admin/products`),
          authFetch(`${API_URL}/api/admin/generated-quotes`)
        ]);
        
        if (quotesRes.ok) setQuotes(await quotesRes.json());
        if (galleryRes.ok) {
          const data = await galleryRes.json();
          setGallery(data.length > 0 ? data : []);
        }
        if (productsRes.ok) {
          const data = await productsRes.json();
          setProducts(data.length > 0 ? data : defaultProducts);
        } else {
          setProducts(defaultProducts);
        }
        if (genQuotesRes.ok) {
          setGeneratedQuotes(await genQuotesRes.json());
        }
      } catch (error) {
        console.error('Error fetching overview data:', error);
      }
    };
    
    fetchOverviewData();
  }, [isAuthenticated, authFetch]);

  // Fetch data based on active tab
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        switch (activeTab) {
          case 'quotes':
            const quotesRes = await authFetch(`${API_URL}/api/admin/quotes`);
            if (quotesRes.ok) setQuotes(await quotesRes.json());
            break;
          case 'gallery':
            const galleryRes = await authFetch(`${API_URL}/api/admin/gallery`);
            if (galleryRes.ok) {
              const data = await galleryRes.json();
              setGallery(data.length > 0 ? data : []);
            }
            break;
          case 'products':
            const productsRes = await authFetch(`${API_URL}/api/admin/products`);
            if (productsRes.ok) {
              const data = await productsRes.json();
              setProducts(data.length > 0 ? data : defaultProducts);
            } else {
              setProducts(defaultProducts);
            }
            break;
          case 'content':
            const contentRes = await authFetch(`${API_URL}/api/admin/content`);
            if (contentRes.ok) {
              const data = await contentRes.json();
              // Convert array to object structure
              const contentObj = {};
              data.forEach(item => {
                if (!contentObj[item.section]) contentObj[item.section] = {};
                contentObj[item.section][item.key] = item.value;
              });
              setContent({ ...defaultContent, ...contentObj });
            } else {
              setContent(defaultContent);
            }
            break;
          case 'translations':
            const enRes = await authFetch(`${API_URL}/api/admin/translations/en`);
            const jaRes = await authFetch(`${API_URL}/api/admin/translations/ja`);
            if (enRes.ok) {
              const enData = await enRes.json();
              // Use static file as default if database is empty
              const enMerged = Object.keys(enData.data || {}).length > 0 ? enData.data : enTranslations;
              setTranslations(prev => ({ ...prev, en: enMerged }));
            } else {
              setTranslations(prev => ({ ...prev, en: enTranslations }));
            }
            if (jaRes.ok) {
              const jaData = await jaRes.json();
              // Use static file as default if database is empty
              const jaMerged = Object.keys(jaData.data || {}).length > 0 ? jaData.data : jaTranslations;
              setTranslations(prev => ({ ...prev, ja: jaMerged }));
            } else {
              setTranslations(prev => ({ ...prev, ja: jaTranslations }));
            }
            break;
          case 'notifications':
            const notifRes = await authFetch(`${API_URL}/api/admin/notification-settings`);
            if (notifRes.ok) {
              const data = await notifRes.json();
              setNotificationSettings(data);
            }
            break;
          case 'business-studio':
            const businessStudioRes = await authFetch(`${API_URL}/api/admin/business-studio/settings`);
            if (businessStudioRes.ok) {
              const data = await businessStudioRes.json();
              setBusinessStudioSettings(data);
            }
            break;
          case 'rentals':
            const rentalSettingsRes = await authFetch(`${API_URL}/api/admin/rentals/settings`);
            if (rentalSettingsRes.ok) {
              const data = await rentalSettingsRes.json();
              setRentalSettings(data);
            }
            const rentalItemsRes = await authFetch(`${API_URL}/api/admin/rentals/items`);
            if (rentalItemsRes.ok) {
              const data = await rentalItemsRes.json();
              setRentalItems(data);
            }
            break;
          case 'quote-generator':
          case 'footer-settings':
            const companyRes = await authFetch(`${API_URL}/api/admin/company-settings`);
            if (companyRes.ok) {
              const data = await companyRes.json();
              setCompanySettings(prev => ({ ...prev, ...data }));
            }
            if (activeTab === 'quote-generator') {
              const genQuotesRes = await authFetch(`${API_URL}/api/admin/generated-quotes`);
              if (genQuotesRes.ok) {
                const data = await genQuotesRes.json();
                setGeneratedQuotes(data);
              }
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [activeTab, isAuthenticated, authFetch]);

  // Initialize content and products on overview load
  useEffect(() => {
    if (activeTab === 'overview' && Object.keys(content).length === 0) {
      setContent(defaultContent);
    }
    if (activeTab === 'overview' && products.length === 0) {
      setProducts(defaultProducts);
    }
  }, [activeTab, content, products]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
    window.location.href = '/admin/login';
  };

  // Quote management
  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await authFetch(`${API_URL}/api/admin/quotes/${quoteId}/status?status=${status}`, {
        method: 'PUT'
      });
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status } : q));
      showToast('Quote status updated');
    } catch (error) {
      showToast('Error updating quote', 'error');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await authFetch(`${API_URL}/api/admin/quotes/${quoteId}`, {
        method: 'DELETE'
      });
      setQuotes(quotes.filter(q => q.id !== quoteId));
      showToast('Quote deleted');
    } catch (error) {
      showToast('Error deleting quote', 'error');
    }
  };

  // Gallery management
  const addGalleryImage = async () => {
    if (!selectedImage) return;
    
    setUploadingImage(true);
    try {
      let imageUrl = '';
      
      if (selectedImage.type === 'file') {
        // Upload file to backend
        const formData = new FormData();
        formData.append('file', selectedImage.file);
        
        const uploadResponse = await authFetch(`${API_URL}/api/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }
        
        const uploadData = await uploadResponse.json();
        imageUrl = `${API_URL}${uploadData.url}`;
      } else {
        // Use URL directly
        imageUrl = selectedImage.url;
      }
      
      // Add to gallery
      const response = await authFetch(`${API_URL}/api/admin/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: imageUrl, title: newImageTitle })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGallery([...gallery, { id: data.id, url: imageUrl, title: newImageTitle }]);
        setSelectedImage(null);
        setNewImageTitle('');
        setAddImageModal(false);
        showToast('Image added to gallery');
      }
    } catch (error) {
      showToast('Error adding image: ' + error.message, 'error');
    }
    setUploadingImage(false);
  };

  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await authFetch(`${API_URL}/api/admin/gallery/${imageId}`, {
        method: 'DELETE'
      });
      setGallery(gallery.filter(img => img.id !== imageId));
      showToast('Image deleted');
    } catch (error) {
      showToast('Error deleting image', 'error');
    }
  };

  // Product management
  const saveProduct = async () => {
    setSaving(true);
    try {
      const product = editModal.data;
      const isNew = product.isNew;
      
      // Generate slug from name if not set
      if (!product.slug && product.name) {
        product.slug = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      
      // Remove the isNew flag before saving
      const productToSave = { ...product };
      delete productToSave.isNew;
      
      if (isNew) {
        // Create new product
        const createRes = await authFetch(`${API_URL}/api/admin/products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productToSave)
        });
        if (createRes.ok) {
          setProducts([...products, productToSave]);
          showToast('Product created successfully');
        } else {
          showToast('Failed to create product', 'error');
        }
      } else {
        // Update existing product
        const response = await authFetch(`${API_URL}/api/admin/products/${product.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productToSave)
        });
        
        if (response.ok) {
          setProducts(products.map(p => p.id === product.id ? productToSave : p));
          showToast('Product updated');
        } else {
          showToast('Failed to update product', 'error');
        }
      }
      setEditModal({ open: false, type: '', data: null });
    } catch (error) {
      showToast('Error saving product', 'error');
    }
    setSaving(false);
  };

  // Content management
  const saveContent = async () => {
    setSaving(true);
    try {
      const { section, data } = editModal.data;
      
      // Save each key-value pair
      for (const [key, value] of Object.entries(data)) {
        await authFetch(`${API_URL}/api/admin/content`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ section, key, value, language: 'en' })
        });
      }
      
      setContent(prev => ({ ...prev, [section]: data }));
      showToast('Content updated');
      setEditModal({ open: false, type: '', data: null });
    } catch (error) {
      showToast('Error saving content', 'error');
    }
    setSaving(false);
  };

  // Translation management
  const saveTranslation = async () => {
    setSaving(true);
    try {
      const { language, data } = editModal.data;
      
      await authFetch(`${API_URL}/api/admin/translations/${language}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language, translations: data })
      });
      
      setTranslations(prev => ({ ...prev, [language]: data }));
      showToast(`${language === 'en' ? 'English' : 'Japanese'} translations updated`);
      setEditModal({ open: false, type: '', data: null });
    } catch (error) {
      showToast('Error saving translations', 'error');
    }
    setSaving(false);
  };

  // Business Studio management functions
  const saveBusinessStudioSettings = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/business-studio/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(businessStudioSettings)
      });
      if (response.ok) {
        showToast('Business Studio settings updated');
      } else {
        showToast('Failed to update Business Studio settings', 'error');
      }
    } catch (error) {
      showToast('Error saving Business Studio settings', 'error');
    }
    setSaving(false);
  };

  // Rental management functions
  const saveRentalSettings = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/rentals/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rentalSettings)
      });
      if (response.ok) {
        showToast('Rental settings updated');
      } else {
        showToast('Failed to update rental settings', 'error');
      }
    } catch (error) {
      showToast('Error saving rental settings', 'error');
    }
    setSaving(false);
  };

  const addRentalItem = async () => {
    if (!newRentalItem.name) {
      showToast('Please enter a name for the rental item', 'error');
      return;
    }
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/rentals/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRentalItem)
      });
      if (response.ok) {
        const data = await response.json();
        setRentalItems([...rentalItems, data.item]);
        setNewRentalItem({
          name: '',
          description: '',
          image: '',
          pricePerDay: '',
          pricePerWeekend: '',
          category: 'wedding',
          available: true
        });
        showToast('Rental item added');
      } else {
        showToast('Failed to add rental item', 'error');
      }
    } catch (error) {
      showToast('Error adding rental item', 'error');
    }
    setSaving(false);
  };

  const updateRentalItem = async (item) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/rentals/items/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      });
      if (response.ok) {
        setRentalItems(rentalItems.map(i => i.id === item.id ? item : i));
        setEditingRentalItem(null);
        showToast('Rental item updated');
      } else {
        showToast('Failed to update rental item', 'error');
      }
    } catch (error) {
      showToast('Error updating rental item', 'error');
    }
    setSaving(false);
  };

  const deleteRentalItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this rental item?')) return;
    try {
      const response = await authFetch(`${API_URL}/api/admin/rentals/items/${itemId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setRentalItems(rentalItems.filter(i => i.id !== itemId));
        showToast('Rental item deleted');
      } else {
        showToast('Failed to delete rental item', 'error');
      }
    } catch (error) {
      showToast('Error deleting rental item', 'error');
    }
  };

  // Handle rental image upload
  const handleRentalImageSelected = async (imageData, isEdit = false) => {
    const setUploading = isEdit ? setEditingRentalImage : setUploadingRentalImage;
    setUploading(true);
    
    try {
      let imageUrl = '';
      
      if (imageData.type === 'file') {
        // Upload file to backend
        const formData = new FormData();
        formData.append('file', imageData.file);
        
        const uploadResponse = await authFetch(`${API_URL}/api/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }
        
        const uploadData = await uploadResponse.json();
        imageUrl = `${API_URL}${uploadData.url}`;
      } else {
        // Use URL directly
        imageUrl = imageData.url;
      }
      
      // Update the appropriate state
      if (isEdit && editingRentalItem) {
        setEditingRentalItem({ ...editingRentalItem, image: imageUrl });
      } else {
        setNewRentalItem({ ...newRentalItem, image: imageUrl });
      }
      
      showToast('Image uploaded successfully');
    } catch (error) {
      showToast('Error uploading image: ' + error.message, 'error');
    }
    
    setUploading(false);
  };

  // ==================== QUOTE GENERATOR FUNCTIONS ====================
  
  const saveCompanySettings = async () => {
    setSavingCompanySettings(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/company-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companySettings)
      });
      if (response.ok) {
        showToast('Company settings saved');
      } else {
        showToast('Failed to save company settings', 'error');
      }
    } catch (error) {
      showToast('Error saving company settings', 'error');
    }
    setSavingCompanySettings(false);
  };

  const addLineItem = () => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      line_items: [...prev.line_items, { 
        id: Date.now().toString(), 
        item_type: '', 
        item_name: '', 
        description: '', 
        quantity: 1, 
        unit_price: 0, 
        image_url: '' 
      }]
    }));
  };

  const removeLineItem = (id) => {
    if (newGeneratedQuote.line_items.length <= 1) return;
    setNewGeneratedQuote(prev => ({
      ...prev,
      line_items: prev.line_items.filter(item => item.id !== id)
    }));
  };

  const updateLineItem = (id, field, value) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateQuoteTotals = () => {
    const subtotal = newGeneratedQuote.line_items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
    const taxAmount = subtotal * (newGeneratedQuote.tax_rate / 100);
    const total = subtotal + newGeneratedQuote.shipping_cost + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const saveGeneratedQuote = async () => {
    setSavingGeneratedQuote(true);
    try {
      const method = editingGeneratedQuote ? 'PUT' : 'POST';
      const url = editingGeneratedQuote 
        ? `${API_URL}/api/admin/generated-quotes/${editingGeneratedQuote.id}`
        : `${API_URL}/api/admin/generated-quotes`;
      
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGeneratedQuote)
      });
      
      if (response.ok) {
        showToast(editingGeneratedQuote ? 'Quote updated' : 'Quote created');
        // Refresh quotes list
        const quotesRes = await authFetch(`${API_URL}/api/admin/generated-quotes`);
        if (quotesRes.ok) {
          setGeneratedQuotes(await quotesRes.json());
        }
        // Reset form
        resetQuoteForm();
      } else {
        showToast('Failed to save quote', 'error');
      }
    } catch (error) {
      showToast('Error saving quote', 'error');
    }
    setSavingGeneratedQuote(false);
  };

  const resetQuoteForm = () => {
    setNewGeneratedQuote({
      quote_request_id: null,
      customer_name: '',
      customer_company: '',
      customer_contact: '',
      customer_address: '',
      customer_phone: '',
      customer_email: '',
      customer_whatsapp: '',
      line_items: [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
      images: [],
      shipping_cost: 0,
      tax_rate: 0,
      processing_time: '10-15 Working days',
      delivery_time: '5-7 working days',
      delivery_method: 'Express',
      payment_terms: 'DAP - Delivered At Place',
      notes: '',
      currency: 'JPY',
      installation_location: 'inside',
      mounting_style: '',
      include_warranty: false,
      include_shipping: true
    });
    setEditingGeneratedQuote(null);
    setShowQuotePreview(false);
  };

  const editGeneratedQuote = async (quote) => {
    // First set the basic quote data
    setNewGeneratedQuote({
      quote_request_id: quote.quote_request_id || null,
      customer_name: quote.customer_name || '',
      customer_company: quote.customer_company || '',
      customer_contact: quote.customer_contact || '',
      customer_address: quote.customer_address || '',
      customer_phone: quote.customer_phone || '',
      customer_email: quote.customer_email || '',
      customer_whatsapp: quote.customer_whatsapp || '',
      line_items: quote.line_items?.length > 0 ? quote.line_items : [{ id: Date.now().toString(), item_type: '', item_name: '', description: '', quantity: 1, unit_price: 0, image_url: '' }],
      images: quote.images || [],
      shipping_cost: quote.shipping_cost || 0,
      tax_rate: quote.tax_rate || 0,
      processing_time: quote.processing_time || '10-15 Working days',
      delivery_time: quote.delivery_time || '5-7 working days',
      delivery_method: quote.delivery_method || 'Express',
      payment_terms: quote.payment_terms || 'DAP - Delivered At Place',
      notes: quote.notes || '',
      currency: quote.currency || 'USD',
      installation_location: quote.installation_location || 'inside',
      mounting_style: quote.mounting_style || '',
      include_warranty: quote.include_warranty || false,
      include_shipping: quote.include_shipping !== false
    });
    setEditingGeneratedQuote(quote);
    
    // If quote is linked to a quote request, fetch and merge the images/attachments
    if (quote.quote_request_id) {
      const linkedRequest = quotes.find(q => q.id === quote.quote_request_id);
      if (linkedRequest) {
        // Merge images from linked quote request if not already in the quote
        const existingImageUrls = new Set((quote.images || []).map(img => img.url));
        const newImages = [];
        
        if (linkedRequest.attachments && linkedRequest.attachments.length > 0) {
          linkedRequest.attachments.forEach((att, idx) => {
            if ((att.type?.startsWith('image/') || att.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && !existingImageUrls.has(att.url)) {
              newImages.push({
                id: Date.now() + idx,
                url: att.url,
                name: att.name || `Image ${idx + 1}`
              });
            }
          });
        }
        
        if (newImages.length > 0) {
          setNewGeneratedQuote(prev => ({
            ...prev,
            images: [...(prev.images || []), ...newImages]
          }));
          showToast(`Loaded ${newImages.length} image(s) from linked quote request`);
        }
      }
    }
  };

  // Load quote for editing and switch to quote generator tab
  const loadQuoteForEditing = (quote) => {
    editGeneratedQuote(quote);
    setActiveTab('quote-generator');
  };

  const deleteGeneratedQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      const response = await authFetch(`${API_URL}/api/admin/generated-quotes/${quoteId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setGeneratedQuotes(generatedQuotes.filter(q => q.id !== quoteId));
        showToast('Quote deleted');
      } else {
        showToast('Failed to delete quote', 'error');
      }
    } catch (error) {
      showToast('Error deleting quote', 'error');
    }
  };

  const prefillFromQuoteRequest = (quoteRequest) => {
    // Collect images from attachments
    const images = [];
    if (quoteRequest.attachments && quoteRequest.attachments.length > 0) {
      quoteRequest.attachments.forEach((att, idx) => {
        if (att.type?.startsWith('image/') || att.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
          images.push({
            id: Date.now() + idx,
            url: att.url,
            name: att.name || `Image ${idx + 1}`
          });
        }
      });
    } else if (quoteRequest.image_url) {
      images.push({
        id: Date.now(),
        url: quoteRequest.image_url,
        name: 'Customer Image'
      });
    }
    
    setNewGeneratedQuote(prev => ({
      ...prev,
      quote_request_id: quoteRequest.id,
      customer_name: quoteRequest.name || '',
      customer_company: quoteRequest.company || '',
      customer_contact: quoteRequest.name || '',
      customer_address: '',
      customer_phone: quoteRequest.phone || '',
      customer_email: quoteRequest.email || '',
      customer_whatsapp: '',
      images: images,
      line_items: [{ 
        id: Date.now().toString(), 
        item_type: quoteRequest.sign_type || '', 
        item_name: quoteRequest.sign_type || 'Custom Sign', 
        description: quoteRequest.message || '', 
        quantity: 1, 
        unit_price: 0, 
        image_url: quoteRequest.image_url || ''
      }],
      currency: 'JPY', // Default to JPY for Okinawa
      // Reset options to defaults for new quotes
      installation_location: 'inside',
      mounting_style: '',
      include_warranty: false,
      include_shipping: true
    }));
    setActiveTab('quote-generator');
    showToast(`Quote form pre-filled for ${quoteRequest.name}`);
  };

  // Currency symbol helper
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'JPY': return '¥';
      case 'EUR': return '€';
      case 'USD':
      default: return '$';
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    if (currency === 'JPY') {
      return `${symbol}${Math.round(amount).toLocaleString()}`;
    }
    return `${symbol}${amount.toFixed(2)}`;
  };

  // Quote image upload functions
  const handleQuoteImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingQuoteImage(true);
    const newImages = [];
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) continue; // 10MB max
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await authFetch(`${API_URL}/api/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          newImages.push({
            id: Date.now() + Math.random(),
            url: data.url,
            name: file.name
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
    
    if (newImages.length > 0) {
      setNewGeneratedQuote(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));
      showToast(`${newImages.length} image(s) uploaded`);
    }
    
    setUploadingQuoteImage(false);
  };

  const handleQuoteImageDrop = (e) => {
    e.preventDefault();
    setIsDraggingQuoteImage(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) handleQuoteImageUpload(files);
  };

  const handleQuoteImageDragOver = (e) => {
    e.preventDefault();
    setIsDraggingQuoteImage(true);
  };

  const handleQuoteImageDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingQuoteImage(false);
  };

  const removeQuoteImage = (imageId) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  // ========== QUOTE REBRANDER FUNCTIONS ==========
  
  const handleRebranderFileSelect = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Please upload a PDF file', 'error');
      return;
    }
    
    setRebranderFile(file);
    setRebranderExtractedData(null);
  };

  const handleRebranderDrop = (e) => {
    e.preventDefault();
    setIsDraggingRebrander(false);
    const file = e.dataTransfer.files[0];
    handleRebranderFileSelect(file);
  };

  const handleRebranderDragOver = (e) => {
    e.preventDefault();
    setIsDraggingRebrander(true);
  };

  const handleRebranderDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingRebrander(false);
  };

  const extractPdfQuote = async () => {
    if (!rebranderFile) {
      showToast('Please upload a PDF first', 'error');
      return;
    }
    
    setRebranderLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', rebranderFile);
      
      const response = await authFetch(`${API_URL}/api/admin/extract-pdf-quote`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setRebranderExtractedData(data.extracted_data);
        showToast('Quote data extracted successfully!');
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to extract data', 'error');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      showToast('Failed to extract quote data', 'error');
    }
    
    setRebranderLoading(false);
  };

  const applyMarkupToPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return numPrice * (1 + rebranderMarkup / 100);
  };

  const transferToQuoteGenerator = () => {
    if (!rebranderExtractedData) {
      showToast('No extracted data to transfer', 'error');
      return;
    }
    
    const data = rebranderExtractedData;
    
    // Apply markup to line items
    const lineItemsWithMarkup = (data.line_items || []).map((item, idx) => ({
      id: (Date.now() + idx).toString(),
      item_type: item.item_type || '',
      item_name: item.item_name || '',
      description: item.description || '',
      quantity: parseInt(item.quantity) || 1,
      unit_price: applyMarkupToPrice(item.unit_price),
      image_url: ''
    }));
    
    // Default to at least one line item
    if (lineItemsWithMarkup.length === 0) {
      lineItemsWithMarkup.push({
        id: Date.now().toString(),
        item_type: '',
        item_name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        image_url: ''
      });
    }
    
    // Transfer to Quote Generator
    setNewGeneratedQuote({
      customer_name: data.buyer_name || '',
      customer_company: data.buyer_company || '',
      customer_contact: data.buyer_name || '',
      customer_address: data.buyer_address || '',
      customer_phone: data.buyer_phone || '',
      customer_email: data.buyer_email || '',
      customer_whatsapp: '',
      line_items: lineItemsWithMarkup,
      images: [],
      shipping_cost: applyMarkupToPrice(data.shipping_cost),
      tax_rate: parseFloat(data.tax_rate) || 0,
      processing_time: data.processing_time || '10-15 Working days',
      delivery_time: data.delivery_time || '5-7 working days',
      delivery_method: data.delivery_method || 'Express',
      payment_terms: data.payment_terms || 'DAP - Delivered At Place',
      notes: data.notes || '',
      currency: rebranderCurrency,
      installation_location: 'inside',
      mounting_style: '',
      include_warranty: false,
      include_shipping: true
    });
    
    // Switch to Quote Generator tab
    setActiveTab('quote-generator');
    showToast(`Quote transferred with ${rebranderMarkup}% markup applied!`);
  };

  const resetRebrander = () => {
    setRebranderFile(null);
    setRebranderExtractedData(null);
    setRebranderMarkup(30);
    setRebranderCurrency('USD');
  };

  // Link quote to an existing quote request
  const linkToQuoteRequest = (quoteRequest) => {
    setNewGeneratedQuote(prev => ({
      ...prev,
      quote_request_id: quoteRequest.id,
      // Optionally pre-fill customer info if not already filled
      customer_name: prev.customer_name || quoteRequest.name || '',
      customer_email: prev.customer_email || quoteRequest.email || '',
      customer_phone: prev.customer_phone || quoteRequest.phone || ''
    }));
    setShowLinkModal(false);
    showToast(`Linked to quote request from ${quoteRequest.name}`);
  };

  // Save current quote as a new quote request
  const saveAsQuoteRequest = async () => {
    if (!newGeneratedQuote.customer_name) {
      showToast('Please enter a customer name first', 'error');
      return;
    }
    
    try {
      // Create a new quote request from the current quote data
      const quoteRequestData = {
        name: newGeneratedQuote.customer_name,
        email: newGeneratedQuote.customer_email || '',
        phone: newGeneratedQuote.customer_phone || '',
        company: newGeneratedQuote.customer_company || '',
        sign_type: newGeneratedQuote.line_items?.[0]?.item_type || 'Custom Sign',
        message: newGeneratedQuote.notes || newGeneratedQuote.line_items?.[0]?.description || '',
        status: 'pending'
      };
      
      const response = await authFetch(`${API_URL}/api/admin/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteRequestData)
      });
      
      if (response.ok) {
        const newRequest = await response.json();
        
        // Now also save the generated quote linked to this new request
        const quoteData = {
          ...newGeneratedQuote,
          quote_request_id: newRequest.id
        };
        
        const saveResponse = await authFetch(`${API_URL}/api/admin/generated-quotes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(quoteData)
        });
        
        if (saveResponse.ok) {
          const savedQuote = await saveResponse.json();
          // Refresh both quotes and generated quotes lists
          fetchQuotes();
          const genQuotesRes = await authFetch(`${API_URL}/api/admin/generated-quotes`);
          if (genQuotesRes.ok) {
            setGeneratedQuotes(await genQuotesRes.json());
          }
          showToast(`Created quote request and saved quote (${savedQuote.quote_number})`);
          resetQuoteForm();
        } else {
          // Quote request was created but generated quote failed
          fetchQuotes();
          showToast('Quote request created, but failed to save generated quote', 'error');
        }
      } else {
        showToast('Failed to create quote request', 'error');
      }
    } catch (error) {
      console.error('Error creating quote request:', error);
      showToast('Error creating quote request', 'error');
    }
  };

  // Get filtered quote requests for link modal
  const getFilteredQuoteRequests = () => {
    if (!linkSearchQuery) return quotes;
    const query = linkSearchQuery.toLowerCase();
    return quotes.filter(q => 
      q.name?.toLowerCase().includes(query) ||
      q.email?.toLowerCase().includes(query) ||
      q.company?.toLowerCase().includes(query) ||
      q.sign_type?.toLowerCase().includes(query)
    );
  };

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'quotes', label: 'Sign Quotes', icon: MessageSquare },
    { id: 'business-inquiries', label: 'Business Inquiries', icon: Building },
    { id: 'brand-boost-inquiries', label: 'Brand Boost Inquiries', icon: TrendingUp },
    { id: 'saved-quotes', label: 'Saved Quotes', icon: FileText },
    { id: 'quote-generator', label: 'Quote Generator', icon: FileText },
    { id: 'quote-rebrander', label: 'Quote Rebrander', icon: RefreshCw },
    { id: 'sales-calculator', label: 'Sales Calculator', icon: Calculator },
    { id: 'neon-generator', label: 'Neon Logo Generator', icon: Sparkles },
    { id: 'flyer-generator', label: 'Flyer Generator', icon: Image },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'business-studio', label: 'Business Studio', icon: Building },
    { id: 'rentals', label: 'Sign Rentals', icon: Calendar },
    { id: 'translations', label: 'Website Text', icon: Edit },
    { id: 'footer-settings', label: 'Footer & Contact', icon: Phone },
    { id: 'disclosure', label: 'Commercial Disclosure', icon: FileText },
    { id: 'privacy', label: 'Privacy Policy', icon: FileText },
    { id: 'analytics', label: 'Analytics & Tracking', icon: TrendingUp },
  ];

  const contentSections = [
    { 
      id: 'hero', 
      name: 'Hero Section', 
      fields: ['badge', 'title1', 'title2', 'subtitle', 'ctaNeon', 'ctaQuote', 'backgroundImage'],
      description: 'Main banner that visitors see first'
    },
    { 
      id: 'features', 
      name: 'Features Section', 
      fields: ['title', 'titleHighlight', 'subtitle', 'feature1_title', 'feature1_description', 'feature2_title', 'feature2_description', 'feature3_title', 'feature3_description', 'feature4_title', 'feature4_description'],
      description: 'Why Choose Us section with 4 feature cards'
    },
    { 
      id: 'testimonials', 
      name: 'Customer Testimonials', 
      fields: ['title', 'subtitle', 'testimonial1_name', 'testimonial1_role', 'testimonial1_text', 'testimonial1_rating', 'testimonial2_name', 'testimonial2_role', 'testimonial2_text', 'testimonial2_rating', 'testimonial3_name', 'testimonial3_role', 'testimonial3_text', 'testimonial3_rating'],
      description: 'Customer reviews displayed on homepage'
    },
    { 
      id: 'footer', 
      name: 'Footer & Contact', 
      fields: ['description', 'address', 'phone', 'email', 'instagramUrl', 'facebookUrl'],
      description: 'Contact info and social media links'
    },
    { 
      id: 'logoDesign', 
      name: 'Logo Design Page', 
      fields: ['heroTitle', 'heroSubtitle'],
      description: 'Logo Design service page content'
    },
    { 
      id: 'brandBoost', 
      name: 'Brand Boost Page', 
      fields: ['heroTitle', 'heroSubtitle'],
      description: 'Business Enhancement service page content'
    }
  ];

  return (
    <div 
      className="min-h-screen flex"
      style={{ backgroundColor: isDark ? '#0a0a0a' : '#f8fafc' }}
    >
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'
        }`}
        style={{
          backgroundColor: isDark ? '#111111' : '#ffffff',
          borderRight: `1px solid ${isDark ? '#222' : '#e2e8f0'}`
        }}
      >
        <div className="p-4 flex items-center justify-between">
          <h2 
            className={`font-bold text-xl ${!sidebarOpen && 'lg:hidden'}`}
            style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
          >
            Admin Panel
          </h2>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden"
            style={{ color: isDark ? '#ffffff' : '#0f172a' }}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="mt-4 px-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-200`}
              style={{
                backgroundColor: activeTab === item.id 
                  ? (isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.1)')
                  : 'transparent',
                color: activeTab === item.id
                  ? (isDark ? '#ff00ff' : '#ff1493')
                  : (isDark ? '#a0a0a0' : '#64748b')
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className={!sidebarOpen ? 'lg:hidden' : ''}>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-0 right-0 px-4 space-y-2">
          <button
            onClick={() => window.open('/', '_blank')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200"
            style={{
              backgroundColor: isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(0, 191, 255, 0.1)',
              color: isDark ? '#00ffff' : '#00bfff'
            }}
          >
            <Eye className="w-5 h-5" />
            <span className={!sidebarOpen ? 'lg:hidden' : ''}>View Site</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444'
            }}
          >
            <LogOut className="w-5 h-5" />
            <span className={!sidebarOpen ? 'lg:hidden' : ''}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg"
              style={{ 
                backgroundColor: isDark ? '#222' : '#f1f5f9',
                color: isDark ? '#ffffff' : '#0f172a'
              }}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 
                className="text-2xl font-bold"
                style={{ color: isDark ? '#ffffff' : '#0f172a' }}
              >
                {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
              </h1>
              <p style={{ color: isDark ? '#666' : '#94a3b8' }}>
                Welcome back, {user?.name || user?.username || 'Admin'}
              </p>
            </div>
          </div>
          {user?.picture && (
            <img 
              src={user.picture} 
              alt="Profile" 
              className="w-10 h-10 rounded-full border-2"
              style={{ borderColor: isDark ? '#ff00ff' : '#ff1493' }}
            />
          )}
        </header>

        {/* Content Area */}
        <div 
          className="rounded-xl p-6"
          style={{
            backgroundColor: isDark ? '#111111' : '#ffffff',
            border: `1px solid ${isDark ? '#222' : '#e2e8f0'}`
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Main Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { 
                        label: 'Pending Quotes', 
                        value: quotes.filter(q => ['new', 'contacted', 'responded', 'customer_replied', 'final'].includes(q.status)).length, 
                        color: '#ff00ff',
                        icon: '⏳'
                      },
                      { 
                        label: 'Quotes Made', 
                        value: generatedQuotes.length, 
                        color: '#00ffff',
                        icon: '📋'
                      },
                      { 
                        label: 'Total Quote Value', 
                        value: `¥${generatedQuotes.reduce((sum, q) => sum + (parseFloat(q.total) || 0), 0).toLocaleString()}`, 
                        color: '#ffff00',
                        icon: '💰'
                      },
                      { 
                        label: 'Revenue (Paid)', 
                        value: (() => {
                          // Get all paid quote request IDs
                          const paidQuoteIds = quotes
                            .filter(q => q.status === 'paid')
                            .map(q => q.id);
                          
                          // Sum up generated quotes linked to paid requests
                          const revenue = generatedQuotes
                            .filter(gq => paidQuoteIds.includes(gq.quote_request_id))
                            .reduce((sum, gq) => sum + (parseFloat(gq.total) || 0), 0);
                          
                          return `¥${revenue.toLocaleString()}`;
                        })(),
                        color: '#00ff88',
                        icon: '📈'
                      },
                    ].map((stat, index) => (
                      <div 
                        key={index}
                        className="p-6 rounded-xl"
                        style={{
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                          border: `2px solid ${stat.color}40`
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <p style={{ color: isDark ? '#666' : '#94a3b8' }}>{stat.label}</p>
                          <span className="text-2xl">{stat.icon}</span>
                        </div>
                        <p 
                          className="text-3xl font-bold mt-2"
                          style={{ color: stat.color }}
                        >
                          {stat.value}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Status Breakdown */}
                  <div 
                    className="p-6 rounded-xl"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  >
                    <h3 className="font-semibold mb-4" style={{ color: isDark ? '#fff' : '#0f172a' }}>
                      Quote Status Breakdown
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                      {[
                        { status: 'new', label: 'New', color: '#3b82f6' },
                        { status: 'contacted', label: 'Contacted', color: '#f59e0b' },
                        { status: 'responded', label: 'Responded', color: '#8b5cf6' },
                        { status: 'customer_replied', label: 'Customer Replied', color: '#06b6d4' },
                        { status: 'final', label: 'Final', color: '#ec4899' },
                        { status: 'paid', label: 'Paid', color: '#10b981' },
                        { status: 'completed', label: 'Completed', color: '#6b7280' },
                      ].map((item) => (
                        <div 
                          key={item.status}
                          className="text-center p-3 rounded-lg"
                          style={{ backgroundColor: `${item.color}20` }}
                        >
                          <p className="text-2xl font-bold" style={{ color: item.color }}>
                            {quotes.filter(q => q.status === item.status).length}
                          </p>
                          <p className="text-xs mt-1" style={{ color: isDark ? '#888' : '#64748b' }}>
                            {item.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Quick Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div 
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc' }}
                    >
                      <p className="text-sm" style={{ color: isDark ? '#666' : '#94a3b8' }}>Gallery Images</p>
                      <p className="text-xl font-bold" style={{ color: isDark ? '#fff' : '#0f172a' }}>{gallery.length}</p>
                    </div>
                    <div 
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc' }}
                    >
                      <p className="text-sm" style={{ color: isDark ? '#666' : '#94a3b8' }}>Products</p>
                      <p className="text-xl font-bold" style={{ color: isDark ? '#fff' : '#0f172a' }}>{products.length}</p>
                    </div>
                    <div 
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc' }}
                    >
                      <p className="text-sm" style={{ color: isDark ? '#666' : '#94a3b8' }}>Total Requests</p>
                      <p className="text-xl font-bold" style={{ color: isDark ? '#fff' : '#0f172a' }}>{quotes.length}</p>
                    </div>
                    <div 
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc' }}
                    >
                      <p className="text-sm" style={{ color: isDark ? '#666' : '#94a3b8' }}>Paid This Month</p>
                      <p className="text-xl font-bold" style={{ color: '#10b981' }}>
                        {quotes.filter(q => {
                          const quoteDate = new Date(q.created_at);
                          const now = new Date();
                          return q.status === 'paid' && 
                            quoteDate.getMonth() === now.getMonth() && 
                            quoteDate.getFullYear() === now.getFullYear();
                        }).length}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sign Quotes Tab */}
              {activeTab === 'quotes' && (
                <SignQuotesTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                  quotes={quotes}
                  setQuotes={setQuotes}
                  generatedQuotes={generatedQuotes}
                  onPrefillFromQuoteRequest={prefillFromQuoteRequest}
                  onSetActiveTab={setActiveTab}
                />
              )}

              {/* Business Inquiries Tab */}
              {activeTab === 'business-inquiries' && (
                <BusinessInquiriesTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                  quotes={quotes}
                  setQuotes={setQuotes}
                />
              )}

              {/* Brand Boost Inquiries Tab */}
              {activeTab === 'brand-boost-inquiries' && (
                <BrandBoostInquiriesTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                  quotes={quotes}
                  setQuotes={setQuotes}
                />
              )}

              {/* Saved Quotes Tab */}
              {activeTab === 'saved-quotes' && (
                <SavedQuotesTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                  generatedQuotes={generatedQuotes}
                  setGeneratedQuotes={setGeneratedQuotes}
                  companySettings={companySettings}
                  onEditQuote={(quote) => {
                    setPendingEditQuote(quote);
                    setActiveTab('quote-generator');
                  }}
                  onDownloadPDF={async (quote) => {
                    try {
                      showToast('Generating PDF...');
                      await generateQuotePDF(quote, companySettings);
                      showToast('PDF downloaded successfully');
                    } catch (err) {
                      console.error('PDF error:', err);
                      showToast('Failed to generate PDF', 'error');
                    }
                  }}
                  onSetActiveTab={setActiveTab}
                />
              )}

              {/* Gallery Tab - With Drag & Drop Reordering */}
              {activeTab === 'gallery' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <p style={{ color: isDark ? '#666' : '#94a3b8' }}>
                        {gallery.length} images in gallery
                      </p>
                      <p className="text-xs mt-1" style={{ color: isDark ? '#444' : '#cbd5e1' }}>
                        💡 Drag and drop images to reorder them
                      </p>
                    </div>
                    <Button
                      onClick={() => setAddImageModal(true)}
                      className="flex items-center gap-2"
                      style={{
                        background: isDark 
                          ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                          : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)'
                      }}
                    >
                      <Plus className="w-4 h-4" /> Add Image
                    </Button>
                  </div>
                  
                  {gallery.length === 0 ? (
                    <div className="text-center py-12">
                      <Image className="w-12 h-12 mx-auto mb-4" style={{ color: isDark ? '#333' : '#e2e8f0' }} />
                      <p style={{ color: isDark ? '#666' : '#94a3b8' }}>No images in gallery. Add your first image!</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {gallery.map((image, index) => (
                        <div 
                          key={image.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', index.toString());
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          onDrop={async (e) => {
                            e.preventDefault();
                            e.currentTarget.style.transform = 'scale(1)';
                            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                            const toIndex = index;
                            if (fromIndex !== toIndex) {
                              const newGallery = [...gallery];
                              const [moved] = newGallery.splice(fromIndex, 1);
                              newGallery.splice(toIndex, 0, moved);
                              setGallery(newGallery);
                              // Save new order to backend
                              try {
                                await authFetch(`${API_URL}/api/admin/gallery/reorder`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ order: newGallery.map(img => img.id) })
                                });
                                showToast('Gallery order updated');
                              } catch (err) {
                                console.error('Failed to save order:', err);
                              }
                            }
                          }}
                          className="relative group rounded-lg overflow-hidden aspect-square cursor-move transition-transform"
                          style={{
                            border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
                          }}
                        >
                          <img 
                            src={image.url} 
                            alt={image.title || 'Gallery'} 
                            className="w-full h-full object-cover pointer-events-none"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/300?text=Image+Error'; }}
                          />
                          {/* Order number badge */}
                          <div 
                            className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{
                              backgroundColor: isDark ? '#ff00ff' : '#ff1493',
                              color: '#fff'
                            }}
                          >
                            {index + 1}
                          </div>
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button
                              onClick={() => deleteGalleryImage(image.id)}
                              className="p-3 bg-red-500 rounded-full text-white hover:bg-red-600"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Products Tab - With Drag & Drop Reordering */}
              {activeTab === 'products' && (
                <div className="space-y-4">
                  {/* Header with Add button */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                        Products ({products.length})
                      </h2>
                      <p className="text-xs mt-1" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                        💡 Drag and drop products to reorder them on the website
                      </p>
                    </div>
                    <Button
                      onClick={() => setEditModal({ 
                        open: true, 
                        type: 'product', 
                        data: { 
                          id: `product-${Date.now()}`,
                          slug: '',
                          name: '', 
                          description: '', 
                          image: '', 
                          features: [],
                          gallery: [],
                          isNew: true
                        } 
                      })}
                      className="flex items-center gap-2"
                      style={{
                        background: isDark 
                          ? 'linear-gradient(135deg, #00ff88 0%, #00ffff 100%)'
                          : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)',
                        color: isDark ? '#000' : '#fff'
                      }}
                    >
                      <Plus className="w-4 h-4" /> Add Product
                    </Button>
                  </div>
                  <div 
                    className="p-4 rounded-lg mb-6"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(255, 20, 147, 0.1)',
                      border: `1px solid ${isDark ? '#ff00ff' : '#ff1493'}`
                    }}
                  >
                    <p style={{ color: isDark ? '#ff00ff' : '#ff1493' }} className="text-sm">
                      <strong>Tip:</strong> Drag products to reorder. Edit details including name, description, features, and images.
                    </p>
                  </div>
                  {(products.length > 0 ? products : defaultProducts).map((product, index) => (
                    <div 
                      key={product.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', index.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = isDark ? '0 0 20px rgba(255, 0, 255, 0.5)' : '0 0 20px rgba(255, 20, 147, 0.3)';
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                        const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                        const toIndex = index;
                        if (fromIndex !== toIndex) {
                          const newProducts = [...products];
                          const [moved] = newProducts.splice(fromIndex, 1);
                          newProducts.splice(toIndex, 0, moved);
                          setProducts(newProducts);
                          // Save new order to backend
                          try {
                            await authFetch(`${API_URL}/api/admin/products/reorder`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ order: newProducts.map(p => p.id) })
                            });
                            showToast('Product order updated');
                          } catch (err) {
                            console.error('Failed to save order:', err);
                          }
                        }
                      }}
                      className="p-4 rounded-lg flex gap-4 items-start cursor-move transition-all"
                      style={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                        border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                      }}
                    >
                      {/* Order number badge */}
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                        style={{
                          backgroundColor: isDark ? '#ff00ff' : '#ff1493',
                          color: '#fff'
                        }}
                      >
                        {index + 1}
                      </div>
                      {/* Product Image Preview */}
                      <div 
                        className="w-20 h-20 rounded-lg flex-shrink-0 overflow-hidden"
                        style={{
                          backgroundColor: isDark ? '#222' : '#e2e8f0',
                          border: `1px solid ${isDark ? '#444' : '#d1d5db'}`
                        }}
                      >
                        {product.image ? (
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-cover pointer-events-none"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8" style={{ color: isDark ? '#444' : '#94a3b8' }} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                          {product.name}
                        </h3>
                        <p style={{ color: isDark ? '#666' : '#94a3b8' }} className="text-sm mt-1">
                          {product.description}
                        </p>
                        {product.features && product.features.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {product.features.slice(0, 3).map((f, i) => (
                              <span 
                                key={i}
                                className="text-xs px-2 py-0.5 rounded"
                                style={{
                                  backgroundColor: isDark ? 'rgba(0, 255, 255, 0.2)' : 'rgba(14, 165, 233, 0.1)',
                                  color: isDark ? '#00ffff' : '#0ea5e9'
                                }}
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setEditModal({ open: true, type: 'product', data: { ...product } })}
                          variant="outline"
                          className="flex items-center gap-2"
                          style={{
                            borderColor: isDark ? '#ff00ff' : '#ff1493',
                            color: isDark ? '#ff00ff' : '#ff1493'
                          }}
                        >
                          <Edit2 className="w-4 h-4" /> Edit
                        </Button>
                        <Button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete "${product.name}"? This cannot be undone.`)) {
                              try {
                                const res = await authFetch(`${API_URL}/api/admin/products/${product.id}`, {
                                  method: 'DELETE'
                                });
                                if (res.ok) {
                                  setProducts(products.filter(p => p.id !== product.id));
                                  showToast('Product deleted successfully');
                                } else {
                                  showToast('Failed to delete product', 'error');
                                }
                              } catch (err) {
                                showToast('Error deleting product', 'error');
                              }
                            }
                          }}
                          variant="outline"
                          className="flex items-center gap-2"
                          style={{
                            borderColor: isDark ? '#ff4444' : '#ef4444',
                            color: isDark ? '#ff4444' : '#ef4444'
                          }}
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Business Studio Tab */}
              {activeTab === 'business-studio' && (
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div 
                    className="p-6 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `2px solid ${businessStudioSettings.enabled ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#ff4444' : '#ef4444')}`
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                          Business Studio Section
                        </h3>
                        <p style={{ color: isDark ? '#888' : '#64748b' }} className="text-sm">
                          {businessStudioSettings.enabled ? 'This page is visible on your website' : 'This page is hidden from your website'}
                        </p>
                      </div>
                      <button
                        onClick={() => setBusinessStudioSettings({ ...businessStudioSettings, enabled: !businessStudioSettings.enabled })}
                        className="px-6 py-2 rounded-full font-semibold transition-all"
                        style={{
                          backgroundColor: businessStudioSettings.enabled ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#333' : '#e2e8f0'),
                          color: businessStudioSettings.enabled ? '#000' : (isDark ? '#888' : '#64748b')
                        }}
                      >
                        {businessStudioSettings.enabled ? 'ON' : 'OFF'}
                      </button>
                    </div>

                    {/* Section Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Page Title
                        </label>
                        <input
                          type="text"
                          value={businessStudioSettings.sectionTitle || ''}
                          onChange={(e) => setBusinessStudioSettings({ ...businessStudioSettings, sectionTitle: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Page Subtitle
                        </label>
                        <input
                          type="text"
                          value={businessStudioSettings.sectionSubtitle || ''}
                          onChange={(e) => setBusinessStudioSettings({ ...businessStudioSettings, sectionSubtitle: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end mt-6">
                      <button
                        onClick={saveBusinessStudioSettings}
                        disabled={saving}
                        className="px-6 py-2 rounded-lg font-semibold transition-all"
                        style={{
                          background: isDark 
                            ? 'linear-gradient(135deg, #00ff88 0%, #00ffff 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)',
                          color: isDark ? '#000' : '#fff',
                          opacity: saving ? 0.5 : 1
                        }}
                      >
                        {saving ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div 
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`
                    }}
                  >
                    <p style={{ color: isDark ? '#00ff88' : '#059669' }} className="text-sm">
                      <strong>Note:</strong> The Business Studio page offers business consulting services including finding commercial space, permits & inspections, business registration, and tax management. Toggle this section off if you don't want to offer these services.
                    </p>
                  </div>
                </div>
              )}

              {/* Rentals Tab */}
              {activeTab === 'rentals' && (
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div 
                    className="p-6 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `2px solid ${rentalSettings.enabled ? (isDark ? '#00ff00' : '#22c55e') : (isDark ? '#ff4444' : '#ef4444')}`
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                          Sign Rentals Section
                        </h3>
                        <p style={{ color: isDark ? '#888' : '#64748b' }} className="text-sm">
                          {rentalSettings.enabled ? 'This section is visible on your website' : 'This section is hidden from your website'}
                        </p>
                      </div>
                      <button
                        onClick={() => setRentalSettings({ ...rentalSettings, enabled: !rentalSettings.enabled })}
                        className="px-6 py-2 rounded-full font-semibold transition-all"
                        style={{
                          backgroundColor: rentalSettings.enabled ? (isDark ? '#00ff00' : '#22c55e') : (isDark ? '#333' : '#e2e8f0'),
                          color: rentalSettings.enabled ? '#000' : (isDark ? '#888' : '#64748b')
                        }}
                      >
                        {rentalSettings.enabled ? 'ON' : 'OFF'}
                      </button>
                    </div>

                    {/* Section Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={rentalSettings.sectionTitle || ''}
                          onChange={(e) => setRentalSettings({ ...rentalSettings, sectionTitle: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Section Subtitle
                        </label>
                        <input
                          type="text"
                          value={rentalSettings.sectionSubtitle || ''}
                          onChange={(e) => setRentalSettings({ ...rentalSettings, sectionSubtitle: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Contact Email
                        </label>
                        <input
                          type="email"
                          value={rentalSettings.contactEmail || ''}
                          onChange={(e) => setRentalSettings({ ...rentalSettings, contactEmail: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          placeholder="rentals@example.com"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Contact Phone
                        </label>
                        <input
                          type="tel"
                          value={rentalSettings.contactPhone || ''}
                          onChange={(e) => setRentalSettings({ ...rentalSettings, contactPhone: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          placeholder="+1 234 567 8900"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={saveRentalSettings}
                      disabled={saving}
                      className="mt-4 flex items-center gap-2"
                      style={{
                        background: isDark 
                          ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                          : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
                        color: '#ffffff'
                      }}
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </div>

                  {/* Add New Rental Item */}
                  <div 
                    className="p-6 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  >
                    <h3 className="text-lg font-semibold mb-4" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                      Add New Rental Item
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Name *
                        </label>
                        <input
                          type="text"
                          value={newRentalItem.name}
                          onChange={(e) => setNewRentalItem({ ...newRentalItem, name: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          placeholder="Mr & Mrs Sign"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Category
                        </label>
                        <select
                          value={newRentalItem.category}
                          onChange={(e) => setNewRentalItem({ ...newRentalItem, category: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        >
                          <option value="wedding">Wedding</option>
                          <option value="party">Party</option>
                          <option value="corporate">Corporate</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      {/* Image Upload Section - spans full width */}
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Rental Item Image
                        </label>
                        
                        {/* Show preview if image is selected */}
                        {newRentalItem.image && (
                          <div className="mb-4 relative inline-block">
                            <img 
                              src={newRentalItem.image} 
                              alt="Preview" 
                              className="w-48 h-32 object-cover rounded-lg"
                              style={{ border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}` }}
                            />
                            <button
                              onClick={() => setNewRentalItem({ ...newRentalItem, image: '' })}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm"
                              style={{ backgroundColor: '#ef4444' }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                        
                        {/* Image Uploader */}
                        {!newRentalItem.image && (
                          <ImageUploader 
                            onImageSelected={(imageData) => handleRentalImageSelected(imageData, false)}
                            isDark={isDark}
                            uploading={uploadingRentalImage}
                          />
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Price Per Day (¥)
                        </label>
                        <input
                          type="text"
                          value={newRentalItem.pricePerDay}
                          onChange={(e) => setNewRentalItem({ ...newRentalItem, pricePerDay: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          placeholder="5,000"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Price Per Weekend (¥)
                        </label>
                        <input
                          type="text"
                          value={newRentalItem.pricePerWeekend}
                          onChange={(e) => setNewRentalItem({ ...newRentalItem, pricePerWeekend: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          placeholder="10,000"
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newRentalItem.available}
                            onChange={(e) => setNewRentalItem({ ...newRentalItem, available: e.target.checked })}
                            className="w-5 h-5 rounded"
                          />
                          <span style={{ color: isDark ? '#a0a0a0' : '#374151' }}>Available</span>
                        </label>
                      </div>
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                          Description
                        </label>
                        <textarea
                          value={newRentalItem.description}
                          onChange={(e) => setNewRentalItem({ ...newRentalItem, description: e.target.value })}
                          className="w-full px-4 py-2 rounded-lg"
                          rows={2}
                          placeholder="Beautiful neon sign perfect for weddings..."
                          style={{
                            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                            border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={addRentalItem}
                      disabled={saving || !newRentalItem.name}
                      className="mt-4 flex items-center gap-2"
                      style={{
                        background: isDark 
                          ? 'linear-gradient(135deg, #00ffff 0%, #0080ff 100%)'
                          : 'linear-gradient(135deg, #0ea5e9 0%, #0080ff 100%)',
                        color: '#ffffff'
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      Add Rental Item
                    </Button>
                  </div>

                  {/* Existing Rental Items */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                      Rental Items ({rentalItems.length})
                    </h3>
                    {rentalItems.length === 0 ? (
                      <p style={{ color: isDark ? '#888' : '#64748b' }}>
                        No rental items yet. Add your first item above!
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {rentalItems.map((item) => (
                          <div 
                            key={item.id}
                            className="p-4 rounded-lg"
                            style={{
                              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                              border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                            }}
                          >
                            {editingRentalItem?.id === item.id ? (
                              // Edit Mode
                              <div className="space-y-3">
                                <input
                                  type="text"
                                  value={editingRentalItem.name}
                                  onChange={(e) => setEditingRentalItem({ ...editingRentalItem, name: e.target.value })}
                                  className="w-full px-3 py-2 rounded"
                                  style={{
                                    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                                    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                    color: isDark ? '#ffffff' : '#0f172a'
                                  }}
                                />
                                
                                {/* Image Upload for Edit Mode */}
                                <div className="space-y-2">
                                  <label className="block text-xs font-medium" style={{ color: isDark ? '#a0a0a0' : '#374151' }}>
                                    Image
                                  </label>
                                  {editingRentalItem.image && (
                                    <div className="relative inline-block">
                                      <img 
                                        src={editingRentalItem.image} 
                                        alt="Preview" 
                                        className="w-32 h-20 object-cover rounded"
                                        style={{ border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}` }}
                                      />
                                      <button
                                        onClick={() => setEditingRentalItem({ ...editingRentalItem, image: '' })}
                                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                                        style={{ backgroundColor: '#ef4444' }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  )}
                                  {!editingRentalItem.image && (
                                    <ImageUploader 
                                      onImageSelected={(imageData) => handleRentalImageSelected(imageData, true)}
                                      isDark={isDark}
                                      uploading={editingRentalImage}
                                    />
                                  )}
                                </div>
                                
                                <textarea
                                  value={editingRentalItem.description}
                                  onChange={(e) => setEditingRentalItem({ ...editingRentalItem, description: e.target.value })}
                                  className="w-full px-3 py-2 rounded"
                                  rows={2}
                                  style={{
                                    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                                    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                    color: isDark ? '#ffffff' : '#0f172a'
                                  }}
                                />
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={editingRentalItem.pricePerDay}
                                    onChange={(e) => setEditingRentalItem({ ...editingRentalItem, pricePerDay: e.target.value })}
                                    className="w-full px-3 py-2 rounded"
                                    placeholder="¥/Day"
                                    style={{
                                      backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                      color: isDark ? '#ffffff' : '#0f172a'
                                    }}
                                  />
                                  <input
                                    type="text"
                                    value={editingRentalItem.pricePerWeekend}
                                    onChange={(e) => setEditingRentalItem({ ...editingRentalItem, pricePerWeekend: e.target.value })}
                                    className="w-full px-3 py-2 rounded"
                                    placeholder="¥/Weekend"
                                    style={{
                                      backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                      color: isDark ? '#ffffff' : '#0f172a'
                                    }}
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <select
                                    value={editingRentalItem.category}
                                    onChange={(e) => setEditingRentalItem({ ...editingRentalItem, category: e.target.value })}
                                    className="flex-1 px-3 py-2 rounded"
                                    style={{
                                      backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                                      color: isDark ? '#ffffff' : '#0f172a'
                                    }}
                                  >
                                    <option value="wedding">Wedding</option>
                                    <option value="party">Party</option>
                                    <option value="corporate">Corporate</option>
                                    <option value="other">Other</option>
                                  </select>
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={editingRentalItem.available}
                                      onChange={(e) => setEditingRentalItem({ ...editingRentalItem, available: e.target.checked })}
                                    />
                                    <span style={{ color: isDark ? '#a0a0a0' : '#374151' }}>Available</span>
                                  </label>
                                </div>
                                <div className="flex gap-2">
                                  <Button onClick={() => updateRentalItem(editingRentalItem)} disabled={saving} size="sm">
                                    <Save className="w-4 h-4 mr-1" /> Save
                                  </Button>
                                  <Button onClick={() => setEditingRentalItem(null)} variant="outline" size="sm">
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              // View Mode
                              <div className="flex gap-4">
                                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: isDark ? '#222' : '#e2e8f0' }}>
                                  {item.image ? (
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Calendar className="w-8 h-8" style={{ color: isDark ? '#444' : '#94a3b8' }} />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <h4 className="font-semibold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>{item.name}</h4>
                                      <span 
                                        className="text-xs px-2 py-0.5 rounded capitalize"
                                        style={{
                                          backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.1)',
                                          color: isDark ? '#ff00ff' : '#ff1493'
                                        }}
                                      >
                                        {item.category}
                                      </span>
                                      <span 
                                        className="text-xs px-2 py-0.5 rounded ml-2"
                                        style={{
                                          backgroundColor: item.available ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                          color: item.available ? '#22c55e' : '#ef4444'
                                        }}
                                      >
                                        {item.available ? 'Available' : 'Booked'}
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-sm mt-1" style={{ color: isDark ? '#888' : '#64748b' }}>
                                    {item.description || 'No description'}
                                  </p>
                                  <div className="flex gap-2 mt-2 text-sm">
                                    {item.pricePerDay && <span style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>Day: ¥{String(item.pricePerDay).replace(/[$¥€]/g, '').trim()}</span>}
                                    {item.pricePerWeekend && <span style={{ color: isDark ? '#ffff00' : '#f59e0b' }}>Weekend: ¥{String(item.pricePerWeekend).replace(/[$¥€]/g, '').trim()}</span>}
                                  </div>
                                  <div className="flex gap-2 mt-3">
                                    <Button onClick={() => setEditingRentalItem({ ...item })} variant="outline" size="sm">
                                      <Edit2 className="w-3 h-3 mr-1" /> Edit
                                    </Button>
                                    <Button onClick={() => deleteRentalItem(item.id)} variant="outline" size="sm" style={{ borderColor: '#ef4444', color: '#ef4444' }}>
                                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="space-y-4">
                  <div 
                    className="p-4 rounded-lg mb-6"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(14, 165, 233, 0.1)',
                      border: `1px solid ${isDark ? '#00ffff' : '#0ea5e9'}`
                    }}
                  >
                    <p style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} className="text-sm">
                      <strong>Tip:</strong> Edit text content for all pages here. For images, use the Gallery or Products tabs. Changes are saved to the database and will persist across sessions.
                    </p>
                  </div>
                  {contentSections.map((section) => (
                    <div 
                      key={section.id}
                      className="p-4 rounded-lg flex justify-between items-center"
                      style={{
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                        border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                      }}
                    >
                      <div>
                        <h3 className="font-semibold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                          {section.name}
                        </h3>
                        <p style={{ color: isDark ? '#888' : '#64748b' }} className="text-sm">
                          {section.description}
                        </p>
                        <p style={{ color: isDark ? '#666' : '#94a3b8' }} className="text-xs mt-1">
                          {section.fields.length} editable fields
                        </p>
                      </div>
                      <Button
                        onClick={() => setEditModal({ 
                          open: true, 
                          type: 'content', 
                          data: { 
                            section: section.id, 
                            name: section.name,
                            fields: section.fields,
                            data: content[section.id] || defaultContent[section.id] || {}
                          } 
                        })}
                        variant="outline"
                        className="flex items-center gap-2"
                        style={{
                          borderColor: isDark ? '#00ffff' : '#00bfff',
                          color: isDark ? '#00ffff' : '#00bfff'
                        }}
                      >
                        <Edit2 className="w-4 h-4" /> Edit Content
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Website Text Tab - Edit ALL text on the website */}
              {activeTab === 'translations' && (
                <div className="space-y-6">
                  {/* Info Banner */}
                  <div 
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      border: `1px solid ${isDark ? '#00ff88' : '#10b981'}`
                    }}
                  >
                    <p style={{ color: isDark ? '#00ff88' : '#10b981' }} className="text-sm">
                      <strong>Edit ALL text on your website here!</strong> This includes the landing page, Brand Boost page, Logo Design page, floating widgets, and all other content. Choose a language below to edit all 290+ text fields.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { code: 'en', name: 'English', flag: '🇺🇸' },
                      { code: 'ja', name: 'Japanese', flag: '🇯🇵' }
                    ].map((lang) => (
                      <div 
                        key={lang.code}
                        className="p-6 rounded-lg"
                        style={{
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                        }}
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-3xl">{lang.flag}</span>
                          <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                            {lang.name}
                          </h3>
                        </div>
                        <p style={{ color: isDark ? '#666' : '#94a3b8' }} className="mb-4">
                          Edit all text content for {lang.name} version
                        </p>
                        <Button
                          onClick={() => setEditModal({ 
                            open: true, 
                            type: 'translation', 
                            data: { 
                              language: lang.code, 
                              name: lang.name,
                              data: translations[lang.code] || {} 
                            } 
                          })}
                          className="w-full flex items-center justify-center gap-2"
                          style={{
                            background: isDark 
                              ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                              : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)'
                          }}
                        >
                          <Edit2 className="w-4 h-4" /> Edit All Website Text
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Quick Reference */}
                  <div 
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#f1f5f9',
                      border: `1px solid ${isDark ? '#222' : '#e2e8f0'}`
                    }}
                  >
                    <h4 className="font-semibold mb-2" style={{ color: isDark ? '#888' : '#64748b' }}>
                      Sections you can edit:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {['Header', 'Hero', 'Products', 'Neon Customizer', 'Gallery', 'Features', 'Testimonials', 'Quote Form', 'Footer', 'Floating Widgets', 'Logo Design (77 fields)', 'Brand Boost (90 fields)'].map((section) => (
                        <span 
                          key={section}
                          className="text-xs px-2 py-1 rounded"
                          style={{
                            backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.1)',
                            color: isDark ? '#ff00ff' : '#ff1493'
                          }}
                        >
                          {section}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  {/* Email Notifications Section */}
                  <div 
                    className="p-6 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Mail className="w-6 h-6" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                        <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                          Email Notifications
                        </h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.email_enabled}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, email_enabled: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </label>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Notification Email Address (where to receive)
                        </label>
                        <input
                          type="email"
                          value={notificationSettings.email_address}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, email_address: e.target.value }))}
                          placeholder="okisignstudio@gmail.com"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Gmail Address (for sending)
                        </label>
                        <input
                          type="email"
                          value={notificationSettings.gmail_address}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, gmail_address: e.target.value }))}
                          placeholder="your-gmail@gmail.com"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Gmail App Password
                        </label>
                        <input
                          type="password"
                          value={notificationSettings.gmail_app_password}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, gmail_app_password: e.target.value }))}
                          placeholder="xxxx xxxx xxxx xxxx"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                        <p className="text-xs mt-1" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                          How to get App Password: Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>Google Account Security</a> → 2-Step Verification → App Passwords → Generate for "Mail"
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      onClick={async () => {
                        setTestingEmail(true);
                        try {
                          const res = await authFetch(`${API_URL}/api/admin/notification-settings/test-email`, {
                            method: 'POST'
                          });
                          if (res.ok) {
                            setToast({ type: 'success', message: 'Test email sent successfully!' });
                          } else {
                            const data = await res.json();
                            setToast({ type: 'error', message: data.detail || 'Failed to send test email' });
                          }
                        } catch (err) {
                          setToast({ type: 'error', message: 'Failed to send test email' });
                        }
                        setTestingEmail(false);
                      }}
                      disabled={testingEmail || !notificationSettings.email_enabled}
                      className="mt-4 flex items-center gap-2"
                      style={{
                        backgroundColor: isDark ? '#333' : '#e2e8f0',
                        color: isDark ? '#fff' : '#0f172a'
                      }}
                    >
                      <TestTube className="w-4 h-4" />
                      {testingEmail ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </div>

                  {/* SMS Notifications Section */}
                  <div 
                    className="p-6 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Phone className="w-6 h-6" style={{ color: isDark ? '#ff00ff' : '#ff1493' }} />
                        <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                          SMS Notifications
                        </h3>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.sms_enabled}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, sms_enabled: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </label>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Your Phone Number (receive SMS)
                        </label>
                        <input
                          type="tel"
                          value={notificationSettings.phone_number}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, phone_number: e.target.value }))}
                          placeholder="+1234567890"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Twilio Phone Number (sender)
                        </label>
                        <input
                          type="tel"
                          value={notificationSettings.twilio_phone_number}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, twilio_phone_number: e.target.value }))}
                          placeholder="+1234567890"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Twilio Account SID
                        </label>
                        <input
                          type="text"
                          value={notificationSettings.twilio_account_sid}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, twilio_account_sid: e.target.value }))}
                          placeholder="ACxxxxxxxxxxxxxxx"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Twilio Auth Token
                        </label>
                        <input
                          type="password"
                          value={notificationSettings.twilio_auth_token}
                          onChange={(e) => setNotificationSettings(prev => ({ ...prev, twilio_auth_token: e.target.value }))}
                          placeholder="xxxxxxxxxxxxxxx"
                          className="w-full p-3 rounded-lg"
                          style={{
                            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#ffffff',
                            border: `1px solid ${isDark ? '#444' : '#d1d5db'}`,
                            color: isDark ? '#ffffff' : '#0f172a'
                          }}
                        />
                        <p className="text-xs mt-1" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                          Get your credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: isDark ? '#ff00ff' : '#ff1493' }}>Twilio Console</a>
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      onClick={async () => {
                        setTestingSms(true);
                        try {
                          const res = await authFetch(`${API_URL}/api/admin/notification-settings/test-sms`, {
                            method: 'POST'
                          });
                          if (res.ok) {
                            setToast({ type: 'success', message: 'Test SMS sent successfully!' });
                          } else {
                            const data = await res.json();
                            setToast({ type: 'error', message: data.detail || 'Failed to send test SMS' });
                          }
                        } catch (err) {
                          setToast({ type: 'error', message: 'Failed to send test SMS' });
                        }
                        setTestingSms(false);
                      }}
                      disabled={testingSms || !notificationSettings.sms_enabled}
                      className="mt-4 flex items-center gap-2"
                      style={{
                        backgroundColor: isDark ? '#333' : '#e2e8f0',
                        color: isDark ? '#fff' : '#0f172a'
                      }}
                    >
                      <TestTube className="w-4 h-4" />
                      {testingSms ? 'Sending...' : 'Send Test SMS'}
                    </Button>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={async () => {
                      setSaving(true);
                      try {
                        const res = await authFetch(`${API_URL}/api/admin/notification-settings`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(notificationSettings)
                        });
                        if (res.ok) {
                          setToast({ type: 'success', message: 'Notification settings saved!' });
                        } else {
                          setToast({ type: 'error', message: 'Failed to save settings' });
                        }
                      } catch (err) {
                        setToast({ type: 'error', message: 'Failed to save settings' });
                      }
                      setSaving(false);
                    }}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 py-4"
                    style={{
                      background: isDark 
                        ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                        : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                      color: isDark ? '#000' : '#fff'
                    }}
                  >
                    <Save className="w-5 h-5" />
                    {saving ? 'Saving...' : 'Save Notification Settings'}
                  </Button>
                </div>
              )}

              {/* Footer & Contact Settings Tab */}
              {activeTab === 'footer-settings' && (
                <div className="space-y-6" data-testid="footer-settings-tab">
                  <div 
                    className="p-6 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <Phone className="w-6 h-6" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
                        <div>
                          <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                            Footer & Contact Information
                          </h2>
                          <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
                            This information appears in the website footer and quote documents
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={saveCompanySettings}
                        disabled={savingCompanySettings}
                        style={{ 
                          background: isDark 
                            ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)' 
                            : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                          color: isDark ? '#000' : '#fff'
                        }}
                      >
                        {savingCompanySettings ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Company Name */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Company Name
                        </label>
                        <input
                          type="text"
                          value={companySettings.company_name}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name: e.target.value }))}
                          placeholder="Your Company Name"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>

                      {/* Company Name (Japanese) */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Company Name (Japanese)
                        </label>
                        <input
                          type="text"
                          value={companySettings.company_name_local}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name_local: e.target.value }))}
                          placeholder="会社名（日本語）"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>

                      {/* Phone Number */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                          Phone Number *
                        </label>
                        <input
                          type="text"
                          value={companySettings.phone}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+81 98-XXX-XXXX"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={companySettings.email}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="your@email.com"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>

                      {/* Address */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>
                          Address *
                        </label>
                        <input
                          type="text"
                          value={companySettings.address}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="1-2-3 Naha City, Okinawa, Japan"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>

                      {/* WhatsApp */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          WhatsApp Number
                        </label>
                        <input
                          type="text"
                          value={companySettings.whatsapp}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, whatsapp: e.target.value }))}
                          placeholder="+81 80-XXXX-XXXX"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>

                      {/* Contact Person */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                          Contact Person
                        </label>
                        <input
                          type="text"
                          value={companySettings.contact_person}
                          onChange={(e) => setCompanySettings(prev => ({ ...prev, contact_person: e.target.value }))}
                          placeholder="John Smith"
                          className="w-full p-3 rounded-lg"
                          style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
                        />
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="mt-6 pt-6 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
                      <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#ffff00' : '#eab308' }}>
                        <Eye className="w-4 h-4" /> Footer Preview
                      </h3>
                      <div 
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: isDark ? '#0a0a0a' : '#1e293b', color: '#fff' }}
                      >
                        <div className="space-y-2 text-sm">
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-pink-500" />
                            {companySettings.address || 'No address set'}
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-cyan-500" />
                            {companySettings.phone || 'No phone number set'}
                          </p>
                          <p className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-green-500" />
                            {companySettings.email || 'No email set'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Commercial Disclosure Tab - Extracted to separate component */}
              {activeTab === 'disclosure' && (
                <CommercialDisclosureTab 
                  isDark={isDark} 
                  authFetch={authFetch} 
                  showToast={showToast}
                />
              )}

              {/* Privacy Policy Tab - Extracted to separate component */}
              {activeTab === 'privacy' && (
                <PrivacyPolicyTab 
                  isDark={isDark} 
                  authFetch={authFetch} 
                  showToast={showToast}
                />
              )}

              {/* Analytics & Tracking Tab */}
              {activeTab === 'analytics' && (
                <AnalyticsTrackingTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                />
              )}

              {/* Quote Generator Tab */}
              {activeTab === 'quote-generator' && (
                <QuoteGeneratorTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                  quotes={quotes}
                  generatedQuotes={generatedQuotes}
                  setGeneratedQuotes={setGeneratedQuotes}
                  companySettings={companySettings}
                  setCompanySettings={setCompanySettings}
                  savingCompanySettings={savingCompanySettings}
                  saveCompanySettings={saveCompanySettings}
                  fetchQuotes={fetchQuotes}
                  initialEditQuote={pendingEditQuote}
                  onEditQuoteConsumed={() => setPendingEditQuote(null)}
                />
              )}


              {/* Flyer Generator Tab */}
              {activeTab === 'flyer-generator' && (
                <FlyerGenerator
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                />
              )}

              {/* Sales Calculator Tab */}
              {activeTab === 'sales-calculator' && (
                <SalesCalculator
                  isDark={isDark}
                  showToast={showToast}
                />
              )}

              {/* Neon Logo Generator Tab */}
              {activeTab === 'neon-generator' && (
                <NeonLogoGenerator
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                />
              )}

              {/* Quote Rebrander Tab */}
              {activeTab === 'quote-rebrander' && (
                <QuoteRebranderTab
                  isDark={isDark}
                  authFetch={authFetch}
                  showToast={showToast}
                  companySettings={companySettings}
                  onTransferToQuoteGenerator={(quoteData, markup) => {
                    sessionStorage.setItem('transferredQuoteData', JSON.stringify(quoteData));
                    setActiveTab('quote-generator');
                    showToast(`Quote transferred to Quote Generator for further editing.`);
                  }}
                />
              )}
            </>
          )}
        </div>
      </main>

      {/* Add Image Modal */}
      <EditModal
        isOpen={addImageModal}
        onClose={() => { setAddImageModal(false); setSelectedImage(null); setNewImageTitle(''); }}
        title="Add Gallery Image"
        onSave={addGalleryImage}
        saving={uploadingImage}
        isDark={isDark}
      >
        <ImageUploader 
          onImageSelected={setSelectedImage}
          isDark={isDark}
          uploading={uploadingImage}
        />
        <InputField
          label="Image Title (optional)"
          value={newImageTitle}
          onChange={setNewImageTitle}
          placeholder="My awesome sign"
          isDark={isDark}
        />
      </EditModal>

      {/* Edit Product Modal */}
      <EditModal
        isOpen={editModal.open && editModal.type === 'product'}
        onClose={() => setEditModal({ open: false, type: '', data: null })}
        title={`Edit ${editModal.data?.name || 'Product'}`}
        onSave={saveProduct}
        saving={saving}
        isDark={isDark}
      >
        {editModal.data && (
          <>
            <InputField
              label="Product Name"
              value={editModal.data.name}
              onChange={(val) => setEditModal(prev => ({ ...prev, data: { ...prev.data, name: val } }))}
              isDark={isDark}
            />
            <InputField
              label="Description"
              value={editModal.data.description}
              onChange={(val) => setEditModal(prev => ({ ...prev, data: { ...prev.data, description: val } }))}
              isDark={isDark}
              multiline
            />
            <InlineImageUploader
              label="Main Product Image (Thumbnail)"
              value={editModal.data.image || ''}
              onChange={(val) => setEditModal(prev => ({ ...prev, data: { ...prev.data, image: val } }))}
              isDark={isDark}
              apiUrl={API_URL}
            />
            
            {/* Gallery Images Section */}
            <div className="mt-6 mb-4">
              <h4 
                className="font-semibold mb-3 flex items-center gap-2"
                style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
              >
                <Image className="w-5 h-5" />
                Product Gallery ({(editModal.data.gallery || []).length} images)
              </h4>
              <p className="text-sm mb-4" style={{ color: isDark ? '#888' : '#64748b' }}>
                These images appear on the product detail page
              </p>
              
              <div className="space-y-4">
                {(editModal.data.gallery || []).map((item, index) => (
                  <div 
                    key={item.id || index}
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : '#f1f5f9',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Image Preview */}
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ backgroundColor: isDark ? '#222' : '#e2e8f0' }}
                          >
                            <Image className="w-8 h-8" style={{ color: isDark ? '#444' : '#94a3b8' }} />
                          </div>
                        )}
                      </div>
                      
                      {/* Editable Fields */}
                      <div className="flex-1 space-y-2">
                        <InputField
                          label={`Image ${index + 1} Title`}
                          value={item.title || ''}
                          onChange={(val) => {
                            const newGallery = [...(editModal.data.gallery || [])];
                            newGallery[index] = { ...newGallery[index], title: val };
                            setEditModal(prev => ({ ...prev, data: { ...prev.data, gallery: newGallery } }));
                          }}
                          isDark={isDark}
                        />
                        <InlineImageUploader
                          label="Image"
                          value={item.image || ''}
                          onChange={(val) => {
                            const newGallery = [...(editModal.data.gallery || [])];
                            newGallery[index] = { ...newGallery[index], image: val };
                            setEditModal(prev => ({ ...prev, data: { ...prev.data, gallery: newGallery } }));
                          }}
                          isDark={isDark}
                          apiUrl={API_URL}
                        />
                      </div>
                      
                      {/* Delete Button */}
                      <Button
                        onClick={() => {
                          const newGallery = (editModal.data.gallery || []).filter((_, i) => i !== index);
                          setEditModal(prev => ({ ...prev, data: { ...prev.data, gallery: newGallery } }));
                        }}
                        variant="ghost"
                        className="p-2"
                        style={{ color: '#ef4444' }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Add New Gallery Image Button */}
                <Button
                  onClick={() => {
                    const newGallery = [...(editModal.data.gallery || [])];
                    const newId = newGallery.length > 0 ? Math.max(...newGallery.map(g => g.id || 0)) + 1 : 1;
                    newGallery.push({ id: newId, title: `Image ${newId}`, image: '' });
                    setEditModal(prev => ({ ...prev, data: { ...prev.data, gallery: newGallery } }));
                  }}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 py-3"
                  style={{
                    borderColor: isDark ? '#00ffff' : '#0ea5e9',
                    color: isDark ? '#00ffff' : '#0ea5e9'
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Add Gallery Image
                </Button>
              </div>
            </div>

            <InputField
              label="Features (one per line)"
              value={(editModal.data.features || []).join('\n')}
              onChange={(val) => setEditModal(prev => ({ 
                ...prev, 
                data: { ...prev.data, features: val.split('\n').filter(f => f.trim()) } 
              }))}
              isDark={isDark}
              multiline
              rows={4}
            />
          </>
        )}
      </EditModal>

      {/* Edit Content Modal */}
      <EditModal
        isOpen={editModal.open && editModal.type === 'content'}
        onClose={() => setEditModal({ open: false, type: '', data: null })}
        title={`Edit ${editModal.data?.name || 'Content'}`}
        onSave={saveContent}
        saving={saving}
        isDark={isDark}
      >
        {editModal.data && editModal.data.fields && (
          <>
            {editModal.data.fields.map((field) => {
              const isImage = field.toLowerCase().includes('image');
              const isLongText = field === 'subtitle' || field === 'description' || field.includes('_text');
              const fieldLabel = field
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/^\w/, c => c.toUpperCase());
              
              // Use drag-and-drop uploader for image fields
              if (isImage) {
                return (
                  <InlineImageUploader
                    key={field}
                    label={fieldLabel}
                    value={editModal.data.data[field] || ''}
                    onChange={(val) => setEditModal(prev => ({ 
                      ...prev, 
                      data: { ...prev.data, data: { ...prev.data.data, [field]: val } } 
                    }))}
                    isDark={isDark}
                    apiUrl={API_URL}
                  />
                );
              }
              
              return (
                <div key={field}>
                  <InputField
                    label={fieldLabel}
                    value={editModal.data.data[field] || ''}
                    onChange={(val) => setEditModal(prev => ({ 
                      ...prev, 
                      data: { ...prev.data, data: { ...prev.data.data, [field]: val } } 
                    }))}
                    isDark={isDark}
                    multiline={isLongText}
                  />
                </div>
              );
            })}
          </>
        )}
      </EditModal>

      {/* Edit Translation Modal - FULLY DYNAMIC - Shows ALL keys */}
      <EditModal
        isOpen={editModal.open && editModal.type === 'translation'}
        onClose={() => setEditModal({ open: false, type: '', data: null })}
        title={`Edit ${editModal.data?.name || ''} Translations - All Website Text`}
        onSave={saveTranslation}
        saving={saving}
        isDark={isDark}
      >
        {editModal.data && (
          <div className="space-y-4">
            <p style={{ color: isDark ? '#666' : '#94a3b8' }} className="text-sm mb-4">
              Edit ALL text on your website. Every field below appears somewhere on your site.
            </p>
            
            {/* Dynamically render ALL sections from translation data */}
            {(() => {
              const sectionConfig = {
                header: { title: '🔗 Header / Navigation', color: isDark ? '#ff00ff' : '#ff1493' },
                hero: { title: '🏠 Hero Section', color: isDark ? '#00ffff' : '#00bfff' },
                productShowcase: { title: '🛍️ Product Showcase', color: isDark ? '#00ff88' : '#10b981' },
                products: { title: '📦 Product Names & Descriptions', color: isDark ? '#ffff00' : '#eab308' },
                productCategory: { title: '📄 Product Detail Pages', color: isDark ? '#ff8800' : '#f97316' },
                neonCustomizer: { title: '✨ Neon Customizer Tool', color: isDark ? '#ff00ff' : '#d946ef' },
                gallery: { title: '🖼️ Gallery Section', color: isDark ? '#00ffff' : '#0ea5e9' },
                features: { title: '⭐ Features / Why Choose Us', color: isDark ? '#00ff88' : '#10b981' },
                testimonials: { title: '💬 Testimonials', color: isDark ? '#ffff00' : '#eab308' },
                quoteForm: { title: '📝 Quote Request Form', color: isDark ? '#ff8800' : '#f97316' },
                footer: { title: '🦶 Footer', color: isDark ? '#888' : '#64748b' },
                widgets: { title: '💬 Floating Widgets', color: isDark ? '#ff00ff' : '#ec4899' },
                logoDesign: { title: '🎨 Logo Design Page (77 fields)', color: isDark ? '#00ffff' : '#0ea5e9' },
                brandBoost: { title: '🚀 Brand Boost Page (90 fields)', color: isDark ? '#00ff88' : '#10b981' },
                common: { title: '🔧 Common / Shared Text', color: isDark ? '#666' : '#94a3b8' }
              };

              const translationData = editModal.data.data || {};

              return Object.keys(sectionConfig).map((sectionKey) => {
                const config = sectionConfig[sectionKey];
                const sectionData = translationData[sectionKey] || {};
                const keys = Object.keys(sectionData).filter(k => typeof sectionData[k] === 'string');
                
                if (keys.length === 0) return null;

                return (
                  <div key={sectionKey} className="p-4 rounded-lg mb-4" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : '#f1f5f9' }}>
                    <h4 className="font-semibold mb-3 flex items-center justify-between" style={{ color: config.color }}>
                      {config.title}
                      <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                        {keys.length} fields
                      </span>
                    </h4>
                    {keys.map((key) => {
                      const value = sectionData[key] || '';
                      const isLongText = key.toLowerCase().includes('desc') || 
                                        key.toLowerCase().includes('subtitle') || 
                                        key.toLowerCase().includes('text') ||
                                        key.toLowerCase().includes('placeholder') ||
                                        key.toLowerCase().includes('items') ||
                                        value.length > 80;
                      return (
                        <InputField
                          key={key}
                          label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace(/([0-9]+)/g, ' $1')}
                          value={value}
                          onChange={(val) => setEditModal(prev => ({
                            ...prev,
                            data: {
                              ...prev.data,
                              data: {
                                ...prev.data.data,
                                [sectionKey]: { ...(prev.data.data?.[sectionKey] || {}), [key]: val }
                              }
                            }
                          }))}
                          isDark={isDark}
                          multiline={isLongText}
                        />
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </EditModal>
    </div>
  );
};

export default AdminDashboard;
